import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { DriverTaskData, AreaTaskData, DetailData } from '../types';
import { useDailyTaskMonitoringQuery } from '../graphql/hooks/useDailyTaskMonitoringQuery';
import { useDailyTaskDetailQuery } from '../graphql/hooks/useDailyTaskDetailQuery';
import { useDailyTaskSummaryAreaQuery } from '../graphql/hooks/useDailyTaskSummaryAreaQuery';
import {
    SEARCH_DELIVERY_FILTER_AREA_OPTIONS,
    SEARCH_DELIVERY_FILTER_AREA_OPTIONS_FOR_DSP,
    SEARCH_DELIVERY_FILTER_DRIVER_OPTIONS,
    SEARCH_DELIVERY_FILTER_DRIVER_OPTIONS_FOR_DSP,
    SEARCH_DELIVERY_FILTER_DSP_OPTIONS,
    type DeliveryFilterOption,
    type SearchDeliveryFilterAreaOptionsResponse,
    type SearchDeliveryFilterAreaOptionsForDspResponse,
    type SearchDeliveryFilterDriverOptionsResponse,
    type SearchDeliveryFilterDriverOptionsForDspResponse,
    type SearchDeliveryFilterDspOptionsResponse,
} from '../../parcelSlaMonitoring/graphql/queries/searchDeliveryFilterOptions';
import { useWarehouseContext } from "../../../../../contexts/WarehouseContext";
import { useCurrentDsp } from '../../../../../hooks/useCurrentDsp';
import { useGetAllAccessWarehousesForRole } from '../../../../../hooks/useWarehouseQuery';
import { GET_ALL_EXCEPTION, ExceptionDictionaryResponse } from '../../../../../graphql/queries/getAllException';
import { DriverFilterOption, buildDriverOptions, rememberDriverNames, toDriverIds } from '../../../../../utils/driverFilterOption';

export type DailyTaskMonitoringPageMode = 'dsp' | 'whs';

export type ViewMode = 'driver' | 'area' | 'dsp' | 'whs';

export interface TaskMonitoringFilters {
    taskDate: dayjs.Dayjs;
    selectedWarehouses?: string[];
    selectedCities: string[];
    selectedDsps: string[];
    selectedAreas: string[];
    /** 司机 accountId 字符串数组（不是姓名，姓名允许重名，见 utils/driverFilterOption） */
    selectedDrivers: string[];
    viewMode: ViewMode;
}

// 工具函数：将名称转换为ID
const convertNamesToIds = (
    names: string[],
    items: Array<{ id: number; name: string }>
): number[] => {
    return names
        .map(name => {
            const item = items.find(item => item.name === name);
            return item ? parseInt(item.id.toString()) : null;
        })
        .filter(id => id !== null) as number[];
};

// 工具函数：获取状态字段映射
const getStatusField = (type: string, t: any): string | undefined => {
    const statusFieldMap: Record<string, string> = {
        'on_time_delivery': 'isOnTimeDelivery',
        'overdue_delivery': 'isOverdueDelivery',
        'transferred_in': 'isTransferredIn',
        'transferred_out': 'isTransferredOut',
        'inDelivery': 'isInDelivery',
        'allocatedParcels': 'isAllocatedParcels',
        'pickedUpFromWarehouse': 'isPickedUpFromWarehouse',
        'warehouseLost': 'isWarehouseLost',
        'returned': 'isReturned',
        'damaged': 'isDamaged',
        'unclaimed': 'isUnclaimed'
    };
    return statusFieldMap[type];
};

// 工具函数：检查是否为状态类型
const isStatusType = (type: string): boolean => {
    const statusTypes = ['on_time_delivery', 'overdue_delivery', 'transferred_in', 'transferred_out', 'inDelivery'];
    return statusTypes.includes(type);
};

export interface UseTaskMonitoringResult {
    filters: TaskMonitoringFilters;
    setFilters: (filters: TaskMonitoringFilters) => void;
    driverData: DriverTaskData[];
    areaData: AreaTaskData[];
    summaryData: any;
    cities: string[];
    warehouses: Array<{ id: string; name: string }>;
    dsps: Array<{ id: number; name: string }>;
    areas: Array<{ id: number; name: string }>;
    drivers: DriverFilterOption[];
    /** 抽屉内司机列表：选中 DSP 时仅显示该 DSP 下的司机，否则与 drivers 相同 */
    drawerDrivers: DriverFilterOption[];
    /** DSP/区域/司机下拉的远程搜索入口（选项已改为按关键词查主库，不再聚合 AI 库汇总表） */
    onDspSearch: (keyword: string) => void;
    onAreaSearch: (keyword: string) => void;
    onDriverSearch: (keyword: string) => void;
    dspOptionsLoading: boolean;
    areaOptionsLoading: boolean;
    driverOptionsLoading: boolean;
    /** 详情抽屉内司机远程搜索（与主列表同口径） */
    onDrawerDriverSearch: (keyword: string) => void;
    drawerDriverOptionsLoading: boolean;
    drawerVisible: boolean;
    drawerTitle: string;
    drawerData: DetailData[];
    drawerColumns: any[];
    setDrawerVisible: (visible: boolean) => void;
    handleDetailClick: (type: string, record: DriverTaskData | AreaTaskData) => void;
    handleUndeliveredDistributionClick: (type: 'undelivered' | 'unclaimed' | 'inDelivery' | 'returned' | 'damaged' | 'warehouseLost') => void;
    handleDeliveryProgressClick: (type: 'allocatedParcels' | 'pickedUpFromWarehouse' | 'delivered') => void;
    statusTypeOptions: Array<{ value: string; label: string }>;
    handleDetailStatusTypeChange: (statusTypes: string[]) => void;
    dataUpdatedAtText: string;
    handlePageChange: (page: number, pageSize?: number) => void;
    handleDetailPageChange: (page: number, pageSize?: number) => void;
    handleSortChange: (sortBy: string, sortDirection: 'ASC' | 'DESC') => void;
    handleDetailSortChange: (sortBy: string, sortDirection: 'ASC' | 'DESC') => void;
    sortConfig: {
        sortBy: string;
        sortDirection: 'ASC' | 'DESC';
    };
    detailSortConfig: {
        sortBy: string;
        sortDirection: 'ASC' | 'DESC';
    };
    detailPagination: any;
    detailLoading: boolean;
    detailFilters: {
        selectedWarehouses?: string[];
        selectedDsps: string[];
        selectedAreas: string[];
        selectedDrivers: string[];
    };
    setDetailFilters: React.Dispatch<React.SetStateAction<{
        selectedWarehouses?: string[];
        selectedDsps: string[];
        selectedAreas: string[];
        selectedDrivers: string[];
    }>>;
    detailStatusTypes: string[];
    loading: boolean;
    pagination: any;
    tablePagination: any;
    orderDetailVisible: boolean;
    orderId: string;
    setOrderDetailVisible: (visible: boolean) => void;
    showOrderDetail: (orderId: number) => void;
    warehouseId: string;
    lastDetailContext: { type: string; record: DriverTaskData | AreaTaskData; filters: TaskMonitoringFilters; statusField?: string; statusFields?: string[] } | null;
    dspMode: boolean;
    currentDspId: string | null;
    getCurrentSummaryDownloadArgs: () => {
        taskDate: string;
        viewMode: string;
        warehouseId?: number;
        warehouseIds?: number[];
        dspIds?: number[];
        areaIds?: number[];
        driverIds?: number[];
        cityNames?: string[];
        sortBy?: string;
        sortDirection?: 'ASC' | 'DESC';
    } | null;
}

// 维度类型常量
const DIMENSION_TYPES = {
    DSP: 'dsp',
    WHS: 'whs',
    AREA: 'area',
    DRIVER: 'driver'
} as const;

export const useDaliyTaskMonitoring = (mode: DailyTaskMonitoringPageMode): UseTaskMonitoringResult => {
    const { t, i18n } = useTranslation();
    const { currentWarehouse } = useWarehouseContext();
    const warehouseId = currentWarehouse?.id!;
    const { dspId, dsp: currentDsp } = useCurrentDsp();
    const isDsp = mode === 'dsp';
    const { warehouses: accessibleWarehouses } = useGetAllAccessWarehousesForRole({ fetchPolicy: 'cache-first' });
    const { data: exceptionData } = useQuery<ExceptionDictionaryResponse>(GET_ALL_EXCEPTION, { fetchPolicy: 'cache-first' });
    const exceptionList = exceptionData?.getExceptionDictionaryForHeadquarters ?? [];
    // 与包裹详情历史一致：只展示当前语言的 secondaryType 文案，不拼接 code
    const getLastFailureReasonDisplayText = useCallback((code: string): string => {
        if (!code) return '';
        const ex = exceptionList.find((e: { exceptionReasonCode: string }) => e.exceptionReasonCode === code);
        if (!ex) return '';
        const lang = i18n.language?.startsWith('zh') ? 'zh' : i18n.language?.startsWith('es') ? 'es' : 'en';
        if (lang === 'zh' && ex.secondaryTypeZh) return ex.secondaryTypeZh;
        if (lang === 'es' && ex.secondaryTypeEs) return ex.secondaryTypeEs;
        if (ex.secondaryTypeEn) return ex.secondaryTypeEn;
        return ex.exceptionReasonCode;
    }, [exceptionList, i18n.language]);

    // Daily 页面固定使用 'daily' 类型
    const pageType: 'daily' = 'daily';
    
    const [filters, setFilters] = useState<TaskMonitoringFilters>({
        taskDate: dayjs(), // 默认时间为今天
        selectedWarehouses: [],
        selectedCities: [],
        selectedDsps: [],
        selectedAreas: [],
        selectedDrivers: [],
        viewMode: 'area' // Daily 页面默认使用 'area'
    });
    
    // 分页状态
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10); // 一页10条
    

    // 排序状态：默认按应派件量从高到低
    const [sortConfig, setSortConfig] = useState<{
        sortBy: string;
        sortDirection: 'ASC' | 'DESC';
    }>({
        sortBy: 'allocatedParcels',
        sortDirection: 'DESC'
    });

    // 详情排序状态 - 默认按状态排序（降序，DELIVERY_FAILED排在前面）
    const [detailSortConfig, setDetailSortConfig] = useState<{
        sortBy: string;
        sortDirection: 'ASC' | 'DESC';
    }>({
        sortBy: 'overallStatus',
        sortDirection: 'DESC'
    });
    
    // 抽屉相关状态
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [drawerTitle, setDrawerTitle] = useState('');
    const [drawerData, setDrawerData] = useState<DetailData[]>([]);
    const [drawerColumns, setDrawerColumns] = useState<any[]>([]);
    const [dataUpdatedAtText, setDataUpdatedAtText] = useState<string>('');
    const [detailStatusTypes, setDetailStatusTypes] = useState<string[]>([]);
    
    // 订单详情相关状态
    const [orderDetailVisible, setOrderDetailVisible] = useState(false);
    const [orderId, setOrderId] = useState<string>('');

    // Daily页面：传递树状节点的 warehouseId
    const getAreaQueryParams = () => {
        return { warehouseId };
    };


    // Daily 页面不使用这些函数，但保留以保持接口兼容性
    const isDspDimension = (): boolean => {
        return false; // Daily 页面没有 DSP 维度
    };

    const isWhsDimension = (): boolean => {
        return false; // Daily 页面没有 WHS 维度
    };

    // 辅助函数：判断是否为司机维度（Daily 页面）
    const isDriverDimension = (): boolean => {
        return filters.viewMode === 'driver';
    };

    // 辅助函数：判断是否为区域维度（Daily 页面）
    const isAreaDimension = (): boolean => {
        return filters.viewMode === 'area';
    };

    // 计算当前维度类型
    // viewMode 映射规则：
    // - Daily 页面：'driver' = 司机维度，'area' = 区域维度
    const getCurrentDimensionType = (): string => {
        // Daily 页面：只支持 'driver' 和 'area'
        return filters.viewMode === 'driver' ? DIMENSION_TYPES.DRIVER : DIMENSION_TYPES.AREA;
    };

    // 使用GraphQL查询 - 区域维度汇总（同一 hook 支持 dsp/whs/hub 三种 mode，Hub 页 dsp/whs 视角也复用）
    const areaQueryParams = getAreaQueryParams();
    const {
        summaryData: areaSummaryData,
        recordData: areaRecordData,
        calculatedAt: areaCalculatedAt,
        loading: areaLoading,
        handleFilter: handleAreaFilter,
        clearData: clearAreaData,
        pagination: areaApiPagination
    } = useDailyTaskSummaryAreaQuery({
        ...areaQueryParams,
        dimensionType: getCurrentDimensionType(),
        mode
    });

    // 使用GraphQL查询 - 司机维度
    const { 
        summaryData: driverSummaryData, 
        recordData: driverRecordData, 
        calculatedAt: driverCalculatedAt,
        loading: driverLoading,
        handleFilter: handleDriverFilter,
        clearData: clearDriverData,
        pagination: driverApiPagination
    } = useDailyTaskMonitoringQuery(mode);

    // 详情查询相关状态
    const [currentDetailType, setCurrentDetailType] = useState<string>('');
    const [currentDetailRecord, setCurrentDetailRecord] = useState<DriverTaskData | AreaTaskData | null>(null);
    const [lastDetailContext, setLastDetailContext] = useState<{ type: string; record: DriverTaskData | AreaTaskData; filters: TaskMonitoringFilters; statusField?: string; statusFields?: string[] } | null>(null);
    
    const skipDetailFiltersEffectRef = React.useRef(false);
    
    // 详情页筛选条件
    const [detailFilters, setDetailFilters] = useState<{
        selectedWarehouses?: string[];
        selectedDsps: string[];
        selectedAreas: string[];
        selectedDrivers: string[];
    }>({
        selectedWarehouses: [],
        selectedDsps: [],
        selectedAreas: [],
        selectedDrivers: []
    });

    const { 
        detailData: newDetailData,
        detailDataRequestId,
        pagination: detailPagination, 
        loading: detailLoading,
        fetchDetailsByStatusField,
        fetchDetailsByStatusFieldForDriver
    } = useDailyTaskDetailQuery(mode);

    /** 用于忽略过期请求的响应，避免状态筛选等操作时竞态导致列表显示错误条数（与 main 行为一致） */
    const detailRequestIdRef = React.useRef(0);

    // 使用 useMemo 稳定 taskDate 字符串，避免每次渲染都生成新字符串导致重复触发
    const taskDateString = useMemo(() => filters.taskDate.format('YYYY-MM-DD'), [filters.taskDate]);

    // DSP/区域/司机选项改为按关键词查主库（原先由 FilterOptions 聚合 AI 库汇总表，下拉常为空）。
    // 下游多处用「名称」反查 id 提交筛选，而搜索结果随关键词变化，
    // 故累积缓存所有见过的 名称→id，替代原先「按维度 + taskDate 缓存整份选项」的做法。
    const dspIdByName = useRef<Map<string, number>>(new Map());
    const areaIdByName = useRef<Map<string, number>>(new Map());
    // 司机下拉的 value 是 accountId，反向缓存用于已选项回显与抽屉标题显示姓名
    const driverNameById = useRef<Map<number, string>>(new Map());

    const [dspKeyword, setDspKeyword] = useState('');
    const [areaKeyword, setAreaKeyword] = useState('');
    const [driverKeyword, setDriverKeyword] = useState('');
    const optionScopeWarehouseIds = useMemo(
        () => (warehouseId ? [Number(warehouseId)] : undefined),
        [warehouseId]
    );

    // DSP 用户的 DSP 下拉是禁用态（数据恒按其自身 DSP 过滤），不需要同仓其它 DSP 的名单
    const { data: dspOptionsData, loading: dspOptionsLoading } = useQuery<
        SearchDeliveryFilterDspOptionsResponse
    >(SEARCH_DELIVERY_FILTER_DSP_OPTIONS, {
        variables: { warehouseIds: optionScopeWarehouseIds, keyword: dspKeyword || null },
        skip: !warehouseId || isDsp
    });

    // DSP 用户的唯一选项就是自己：既让禁用框显示自身 DSP 名称，
    // 也让 dspIdByName 能解析该名称（否则 hasUnmappedSelection 门禁会拦住主查询）
    const ownDspOptions = useMemo(
        () => (currentDsp?.id != null && currentDsp?.name
            ? [{ id: Number(currentDsp.id), name: currentDsp.name }]
            : undefined),
        [currentDsp?.id, currentDsp?.name]
    );

    const { data: areaOptionsData, loading: areaOptionsLoading } = useQuery<
        SearchDeliveryFilterAreaOptionsResponse | SearchDeliveryFilterAreaOptionsForDspResponse
    >(isDsp ? SEARCH_DELIVERY_FILTER_AREA_OPTIONS_FOR_DSP : SEARCH_DELIVERY_FILTER_AREA_OPTIONS, {
        variables: { warehouseIds: optionScopeWarehouseIds, keyword: areaKeyword || null },
        skip: !warehouseId
    });

    // 主筛选 DSP→司机级联，与抽屉的 drawerCascadedDspIds 口径一致。
    // 不传 dspIds 时后端会按仓库反查该仓关联的全部 DSP（searchDriverOptions:171），
    // 下拉会列出其它 DSP 的司机；DSP 视角还构成越权展示。
    const mainCascadedDspIds = useMemo(() => {
        if (isDsp) return dspId != null ? [Number(dspId)] : undefined;
        const ids = filters.selectedDsps
            .map((name) => dspIdByName.current.get(name))
            .filter((id): id is number => id != null)
            .map(Number);
        return ids.length > 0 ? ids : undefined;
    }, [isDsp, dspId, filters.selectedDsps]);

    const { data: driverOptionsData, loading: driverOptionsLoading } = useQuery<
        SearchDeliveryFilterDriverOptionsResponse | SearchDeliveryFilterDriverOptionsForDspResponse
    >(isDsp ? SEARCH_DELIVERY_FILTER_DRIVER_OPTIONS_FOR_DSP : SEARCH_DELIVERY_FILTER_DRIVER_OPTIONS, {
        variables: {
            warehouseIds: optionScopeWarehouseIds,
            dspIds: mainCascadedDspIds,
            keyword: driverKeyword || null
        },
        skip: !warehouseId
    });

    const searchedDsps = isDsp
        ? ownDspOptions
        : dspOptionsData?.searchDeliveryFilterDspOptions;
    const searchedAreas = isDsp
        ? (areaOptionsData as SearchDeliveryFilterAreaOptionsForDspResponse | undefined)?.searchDeliveryFilterAreaOptionsForDsp
        : (areaOptionsData as SearchDeliveryFilterAreaOptionsResponse | undefined)?.searchDeliveryFilterAreaOptions;
    const searchedDrivers = isDsp
        ? (driverOptionsData as SearchDeliveryFilterDriverOptionsForDspResponse | undefined)?.searchDeliveryFilterDriverOptionsForDsp
        : (driverOptionsData as SearchDeliveryFilterDriverOptionsResponse | undefined)?.searchDeliveryFilterDriverOptions;

    (searchedDsps ?? []).forEach((option) => dspIdByName.current.set(option.name, option.id));
    (searchedAreas ?? []).forEach((option) => areaIdByName.current.set(option.name, option.id));
    rememberDriverNames(searchedDrivers, driverNameById.current);

    /** 行下钻会把行上的 DSP/区域/司机写进抽屉筛选，这些行通常不在搜索结果里，故先把行上的名称与 id 记入缓存 */
    const rememberRecordOptionIds = (record: DriverTaskData | AreaTaskData) => {
        const rowDspId = record.dspId;
        if (record.dsp && rowDspId) dspIdByName.current.set(record.dsp, Number(rowDspId));
        if ('area' in record && record.area && record.areaId) areaIdByName.current.set(record.area, Number(record.areaId));
        if ('driverName' in record && record.driverName && record.driverId) {
            driverNameById.current.set(Number(record.driverId), record.driverName);
        }
    };

    /** 搜索结果并入「已选项」，否则 antd Select 无法回显已选标签 */
    const mergeSelectedIntoOptions = (
        searched: DeliveryFilterOption[] | undefined,
        selectedNames: string[] | undefined,
        idByName: Map<string, number>
    ): DeliveryFilterOption[] => {
        const merged = [...(searched ?? [])];
        (selectedNames ?? []).forEach((name) => {
            if (!name || merged.some((option) => option.name === name)) return;
            merged.push({ id: idByName.get(name) ?? -1, name });
        });
        return merged;
    };

    // 城市下拉在三个筛选组件中均未渲染，原 FilterOptions 的 cities 属于死字段，此处不再提供
    const cities: string[] = [];
    // 抽屉内的已选项由表格行下钻写入，未必命中按关键词搜索的选项，需一并并入，否则下游「名称→id」反查不到会丢掉筛选条件
    const dsps = useMemo(
        () => mergeSelectedIntoOptions(searchedDsps, [...filters.selectedDsps, ...(detailFilters.selectedDsps ?? [])], dspIdByName.current),
        [searchedDsps, filters.selectedDsps, detailFilters.selectedDsps]
    );
    const areas = useMemo(
        () => mergeSelectedIntoOptions(searchedAreas, [...filters.selectedAreas, ...(detailFilters.selectedAreas ?? [])], areaIdByName.current),
        [searchedAreas, filters.selectedAreas, detailFilters.selectedAreas]
    );
    const drivers = useMemo(
        () => buildDriverOptions(
            searchedDrivers,
            [...filters.selectedDrivers, ...(detailFilters.selectedDrivers ?? [])],
            driverNameById.current
        ),
        [searchedDrivers, filters.selectedDrivers, detailFilters.selectedDrivers]
    );

    // 抽屉内 DSP-司机级联：选中 DSP 时，司机下拉只显示该 DSP 下的司机
    const drawerCascadedDspIds = useMemo(() => {
        if (!detailFilters.selectedDsps || detailFilters.selectedDsps.length === 0) return null;
        const ids = detailFilters.selectedDsps
            .map(name => dsps.find((d: { id: number; name: string }) => d.name === name)?.id)
            .filter((id): id is number => id != null);
        return ids.length > 0 ? ids : null;
    }, [detailFilters.selectedDsps, dsps]);

    // 抽屉内 DSP→司机级联同样走主库按需搜索，与主列表口径一致
    const [drawerDriverKeyword, setDrawerDriverKeyword] = useState('');
    const { data: drawerCascadedDriverData, loading: drawerDriverOptionsLoading } = useQuery<
        SearchDeliveryFilterDriverOptionsResponse | SearchDeliveryFilterDriverOptionsForDspResponse
    >(isDsp ? SEARCH_DELIVERY_FILTER_DRIVER_OPTIONS_FOR_DSP : SEARCH_DELIVERY_FILTER_DRIVER_OPTIONS, {
        variables: {
            warehouseIds: optionScopeWarehouseIds,
            dspIds: drawerCascadedDspIds ?? undefined,
            keyword: drawerDriverKeyword || null
        },
        skip: !warehouseId || !drawerVisible
    });

    const drawerDrivers = useMemo(() => {
        const searched = isDsp
            ? (drawerCascadedDriverData as SearchDeliveryFilterDriverOptionsForDspResponse | undefined)?.searchDeliveryFilterDriverOptionsForDsp
            : (drawerCascadedDriverData as SearchDeliveryFilterDriverOptionsResponse | undefined)?.searchDeliveryFilterDriverOptions;
        rememberDriverNames(searched, driverNameById.current);
        if (!searched) return drivers;
        return buildDriverOptions(searched, detailFilters.selectedDrivers, driverNameById.current);
    }, [drawerCascadedDriverData, drivers, detailFilters.selectedDrivers, isDsp]);

    // 当DSP经理登录且DSP选项加载完成时，自动设置默认DSP（汇总页面）
    // 依赖里带 selectedDsps.length 是为了自愈：切日期（handleFiltersUpdate）与切维度
    // （handleViewModeChange）都会清空 selectedDsps，而 DSP 用户的下拉是禁用态、无法手动选回，
    // 清空后必须重新填上自身 DSP，否则筛选框会一直显示占位符。
    // 不会死循环：填上后 length 变 1，下次进来被 length === 0 挡住。
    useEffect(() => {
        if (isDsp && currentDsp && currentDsp.name && dsps.length > 0) {
            // 检查当前DSP是否在选项中
            const currentDspInOptions = dsps.find((d: any) => d.id === currentDsp.id || d.name === currentDsp.name);
            if (currentDspInOptions && filters.selectedDsps.length === 0) {
                setFilters(prev => ({
                    ...prev,
                    selectedDsps: [currentDsp.name]
                }));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDsp, currentDsp, dsps.length, filters.selectedDsps.length]);

    // 当DSP经理登录且详情DSP选项加载完成时，自动设置默认DSP（详情页面）
    useEffect(() => {
        if (isDsp && currentDsp && currentDsp.name && detailFilters.selectedDsps.length === 0) {
            // 优先使用详情选项，如果没有则使用主列表选项
            const availableDsps = dsps;
            
            if (availableDsps.length > 0) {
                // 检查当前DSP是否在选项中
                const currentDspInOptions = availableDsps.find((d: any) => d.id === currentDsp.id || d.name === currentDsp.name);
                if (currentDspInOptions) {
                    setDetailFilters(prev => ({
                        ...prev,
                        selectedDsps: [currentDsp.name]
                    }));
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDsp, currentDsp, dsps.length]);

    // 处理司机维度数据（仅在司机维度模式下处理）
    const driverData: DriverTaskData[] = useMemo(() => {
        // 如果当前不是司机维度模式，返回空数组，避免不必要的处理
        if (filters.viewMode !== 'driver' || driverRecordData.length === 0) {
            return [];
        }
        return driverRecordData
            .filter(record => record.driverId && record.driverId.toString() !== '0' && record.driverId.toString() !== '999')
            .map(record => {
                const onTimeDelivery = Number((record as any).onTimeDelivery) || 0;
                const overdueDelivery = Number((record as any).overdueDelivery) || 0;
                const deliveredCount = (record as any).deliveredCount != null 
                    ? Number((record as any).deliveredCount) 
                    : 0;
                const pickedUpFromWarehouse = Number((record as any).pickedUpFromWarehouse) || 0;
                const transferredIn = Number((record as any).transferredIn) || 0;
                const allocatedParcels = Number((record as any).allocatedParcels) || 0;
                const scheduledParcels = allocatedParcels + transferredIn;
                const deliveryRate = scheduledParcels > 0 
                    ? Number((deliveredCount / scheduledParcels * 100).toFixed(2))
                    : 0;
                const returned = Number((record as any).returned) || 0;
                const damaged = Number((record as any).damaged) || 0;
                const failedDeliveryRate = pickedUpFromWarehouse > 0
                    ? Number(((returned + damaged) / pickedUpFromWarehouse * 100).toFixed(2))
                    : 0;
                
                let dspName = (record as any).dspName;
                let dspId = (record as any).dspId;
                if (!dspName && dspId) {
                    const dspFromOptions = dsps.find((d: { id: number; name: string }) => d.id === dspId);
                    dspName = dspFromOptions?.name || `DSP-${dspId}`;
                } else if (!dspName) {
                    dspName = '-';
                }
                // 如果 dspId 不存在，尝试从 dsp 名称查找
                if (!dspId || dspId <= 0) {
                    if (dspName && dspName !== '-') {
                        const dspFromOptions = dsps.find((d: { id: number; name: string }) => d.name === dspName);
                        dspId = dspFromOptions?.id;
                    }
                }
                const driverName = (record as any).driverName || `Driver-${record.driverId}`;
                const warehouseName = (record as any).warehouseName || currentWarehouse?.name || undefined;
                
                return {
                    id: Number(record.id) || 0,
                    driverId: record.driverId?.toString() || '',
                    driverName: driverName,
                    warehouseName,
                    dsp: dspName,
                    dspId: dspId, // 确保包含 dspId，用于下钻时传递
                    allocatedParcels: Number((record as any).allocatedParcels) || 0,
                    pickedUpFromWarehouse,
                    warehouseLost: Number((record as any).warehouseLost) || 0,
                    inDelivery: Number((record as any).inDelivery) || 0,
                    onTimeDelivery,
                    overdueDelivery,
                    deliveredCount,
                    unclaimedCount: Number((record as any).unclaimedCount) || 0,
                    noAscanDelivery: Number((record as any).noAscanDelivery) || 0,
                    transferredIn: Number((record as any).transferredIn) || 0,
                    transferredOut: Number((record as any).transferredOut) || 0,
                    returned,
                    damaged,
                    lastDeliveryTime: (record as any).lastDeliveryTime || undefined,
                    dwellTime: (record as any).dwellTime || undefined,
                    deliveryRate,
                    failedDeliveryRate
                };
            });
    }, [driverRecordData, dsps, filters.viewMode, currentWarehouse?.name]);

    // 司机维度数据处理完成

    // 直接使用区域汇总数据，不进行聚合
    const areaData: AreaTaskData[] = areaRecordData.map((record, index) => {
        // 优先使用后端返回的 dspName，如果为空则从筛选选项中查找
        let dspName = record.dspName;
        if (!dspName && record.dspId) {
            const dspFromOptions = dsps.find((d: { id: number; name: string }) => d.id === record.dspId);
            dspName = dspFromOptions?.name || `DSP-${record.dspId}`;
        } else if (!dspName) {
            dspName = '-';
        }
        
        return {
            id: record.id || `area-${record.areaId}-${record.dspId}-${index}`,
            area: record.areaName || `Area-${record.areaId}`,
            dsp: dspName || '-',
            warehouseName: (record as any).warehouseName || currentWarehouse?.name || undefined,
            warehouseId: Number((record as any).warehouseId) || undefined,
            areaId: record.areaId,
            dspId: record.dspId,
            allocatedParcels: Number(record.allocatedParcels) || 0,
            pickedUpFromWarehouse: Number(record.pickedUpFromWarehouse) || 0,
            warehouseLost: Number(record.warehouseLost) || 0,
            inDelivery: Number(record.inDelivery) || 0,
            onTimeDelivery: Number(record.onTimeDelivery) || 0,
            overdueDelivery: Number(record.overdueDelivery) || 0,
            deliveredCount: Number((record as any).deliveredCount) || 0,
            unclaimedCount: Number((record as any).unclaimedCount) || 0,
            noAscanDelivery: Number((record as any).noAscanDelivery) || 0,
            transferredIn: Number(record.transferredIn) || 0,
            transferredOut: Number(record.transferredOut) || 0,
            returned: Number(record.returned) || 0,
            damaged: Number(record.damaged) || 0,
            deliveryRate: (() => {
                const deliveredCount = Number((record as any).deliveredCount) || 0;
                const transferredIn = Number(record.transferredIn) || 0;
                const allocatedParcels = Number(record.allocatedParcels) || 0;
                const scheduledParcels = allocatedParcels + transferredIn;

                if (scheduledParcels === 0) return 0;
                return Number((deliveredCount / scheduledParcels * 100).toFixed(2));
            })(),
            failedDeliveryRate: (() => {
                const returned = Number(record.returned) || 0;
                const damaged = Number(record.damaged) || 0;
                const pickedUpFromWarehouse = Number(record.pickedUpFromWarehouse) || 0;
            
                if (pickedUpFromWarehouse === 0) return 0;
                return Number(((returned + damaged) / pickedUpFromWarehouse * 100).toFixed(2));
            })()
        };
    });

    // Hub 页面使用 areaSummaryData，Daily 页面根据 viewMode 选择
    const currentSummaryData = isDriverDimension() ? driverSummaryData : areaSummaryData;

    // 数据更新时间（用于页面展示：数据更新于 XXX）——使用后端返回的 calculatedAt
    useEffect(() => {
        const calculatedAt = isDriverDimension() ? driverCalculatedAt : areaCalculatedAt;
        if (!calculatedAt) {
            setDataUpdatedAtText('');
            return;
        }
        try {
            const d = dayjs(calculatedAt);
            setDataUpdatedAtText(d.isValid() ? d.format('YYYY-MM-DD HH:mm:ss') : String(calculatedAt));
        } catch {
            setDataUpdatedAtText(String(calculatedAt));
        }
    }, [filters.viewMode, driverCalculatedAt, areaCalculatedAt]);

    // 状态文本转换
    const getStatusText = (status: number): string => {
        const statusMap: { [key: number]: string } = {
            1: t('wms.delivery.dailyTaskMonitoring.status.inProgress'),
            2: t('wms.delivery.dailyTaskMonitoring.status.delivered'),
            3: t('wms.delivery.dailyTaskMonitoring.status.returned'),
            4: t('wms.delivery.dailyTaskMonitoring.status.damaged'),
            5: t('wms.delivery.dailyTaskMonitoring.status.lost')
        };
        return statusMap[status] || t('wms.delivery.dailyTaskMonitoring.status.unknown');
    };

    // 显示订单详情
    const showOrderDetail = (orderId: number) => {
        if (orderId) {
            setOrderId(orderId.toString());
            setOrderDetailVisible(true);
        }
    };

    // 根据类型获取列定义
    const getColumnsByType = (type: string, detailType?: string) => {
        const isDriverDimension = (detailType || currentDetailType) === 'driver';
        const driverColumnTitle = isDriverDimension 
            ? t('wms.delivery.dailyTaskMonitoring.detailColumns.deliveryDriver')
            : t('wms.delivery.dailyTaskMonitoring.detailColumns.driver');
        
        const baseColumns = [
            { 
                title: t('wms.delivery.dailyTaskMonitoring.detailColumns.trackingNumber'), 
                dataIndex: 'tracking_number', 
                key: 'tracking_number',
                render: (text: string, record: any) => React.createElement('a', {
                    href: '#',
                    onClick: (e: React.MouseEvent) => {
                            e.preventDefault();
                            showOrderDetail(record.order_id);
                    },
                    style: { color: '#1890ff' }
                }, text)
            },
            { title: t('wms.delivery.dailyTaskMonitoring.detailColumns.dsp'), dataIndex: 'dsp_name', key: 'dsp_name', sortable: true, render: (text: string) => (text && text.trim().length > 0 ? text : '-') },
            { title: t('wms.delivery.dailyTaskMonitoring.detailColumns.area'), dataIndex: 'area_name', key: 'area_name', sortable: true, render: (text: string) => (text && text.trim().length > 0 ? text : '-') },
            { title: driverColumnTitle, dataIndex: 'driver_name', key: 'driver_name', sortable: true, render: (text: string) => (text && text.trim().length > 0 ? text : '-') },
            { title: t('wms.delivery.dailyTaskMonitoring.detailColumns.taskDate'), dataIndex: 'task_date', key: 'task_date', render: (text: string) => text || '-' },
            { title: t('wms.delivery.dailyTaskMonitoring.detailColumns.status'), dataIndex: 'status', key: 'status', sortable: true, render: (text: string) => text || '-' },
            { title: t('wms.delivery.dailyTaskMonitoring.detailColumns.lastFailureReason'), dataIndex: 'last_failure_reason', key: 'last_failure_reason', render: (text: string, record: DetailData) => { const code = record.exception_reason_code; const displayText = code ? getLastFailureReasonDisplayText(code) : ''; return (displayText && displayText.trim().length > 0) ? displayText : (text && text.trim().length > 0 ? text : '-'); } },
            {
                title: t('wms.delivery.dailyTaskMonitoring.detailColumns.latestPhysicalTrackingUpdatedAt'),
                dataIndex: 'latest_physical_tracking_updated_at',
                key: 'latest_physical_tracking_updated_at',
                render: (text: string) => {
                    if (!text) return '-';
                    const formatted = dayjs(text).isValid() ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : text;
                    return formatted;
                }
            }
        ];

        if (type === 'delivered') {
            return [...baseColumns, { title: t('wms.delivery.dailyTaskMonitoring.detailColumns.deliveredTime'), dataIndex: 'delivered_time', key: 'delivered_time', sortable: true }];
        }

        return baseColumns;
    };

    // 点击数字查看详情
    const handleDetailClick = async (type: string, record: DriverTaskData | AreaTaskData) => {
        rememberRecordOptionIds(record);

        // 根据筛选条件构建查询参数
        const selectedDspIds = filters.selectedDsps.length > 0
            ? filters.selectedDsps.map(dspName => {
                const dsp = dsps.find((d: any) => d.name === dspName);
                return dsp ? dsp.id : null;
            }).filter(id => id !== null) as number[]
            : undefined;

        const selectedAreaIds = filters.selectedAreas.length > 0
            ? filters.selectedAreas.map(areaName => {
                const area = areas.find((a: any) => a.name === areaName);
                return area ? area.id : null;
            }).filter(id => id !== null) as number[]
            : undefined;

        const selectedCities = filters.selectedCities.length > 0 
            ? filters.selectedCities
            : undefined;

        // 判断是司机维度还是区域维度
        const isDriverDimension = 'driverName' in record;
        
        const typeMap: { [key: string]: string } = {
            'total_parcels': t('wms.delivery.dailyTaskMonitoring.areaColumns.totalScheduledParcels'),
            // 仅“已妥投”（避免显示为“已妥投/实际领取”）
            'delivered': t('wms.delivery.dailyTaskMonitoring.tableColumns.delivered'),
            'in_progress': t('wms.delivery.dailyTaskMonitoring.areaColumns.delivering'),
            'inDelivery': t('wms.delivery.dailyTaskMonitoring.areaColumns.delivering'),
            'returned': t('wms.delivery.dailyTaskMonitoring.areaColumns.failedReturned'),
            // 与表格/未妥投分布保持一致：派送失败未回仓
            'damaged': t('wms.delivery.dailyTaskMonitoring.tableColumns.failedNotReturned'),
            'lost': t('wms.delivery.dailyTaskMonitoring.tableColumns.failedNotReturned'),
            'on_time_delivery': t('wms.delivery.dailyTaskMonitoring.areaColumns.onTimeDelivery'),
            'overdue_delivery': t('wms.delivery.dailyTaskMonitoring.areaColumns.delayedDelivery'),
            'transferred_in': t('wms.delivery.dailyTaskMonitoring.areaColumns.transferInToday'),
            'transferred_out': t('wms.delivery.dailyTaskMonitoring.areaColumns.transferOutToday'),
            // 与表格列标题保持一致：应派件量
            'allocatedParcels': t('wms.delivery.dailyTaskMonitoring.tableColumns.scheduledParcels'),
            'pickedUpFromWarehouse': t('wms.delivery.dailyTaskMonitoring.summary.actualPickedUp'),
            // 与表格列标题保持一致：仓内疑似丢失
            'warehouseLost': t('wms.delivery.dailyTaskMonitoring.tableColumns.warehouseLost'),
            'unclaimed': t('wms.delivery.dailyTaskMonitoring.areaColumns.unclaimed'),
            'no_ascan_delivery': t('wms.delivery.dailyTaskMonitoring.areaColumns.noAscanDelivery')
        };

        const statusMap: { [key: string]: number } = {
            'delivered': 2,      // 已妥投
            'in_progress': 1,    // 进行中
            // 'returned': 3,    // 已退件 - 已移到 deliveryEfficiencyStatusMap
            'lost': 5            // 丢失
        };
        
        // 配送效率状态映射
        const deliveryEfficiencyStatusMap: { [key: string]: string } = {
            'on_time_delivery': 'isOnTimeDelivery',    // 准时妥投
            'overdue_delivery': 'isOverdueDelivery',  // 超时妥投
            'transferred_in': 'isTransferredIn',      // 当日转入
            'transferred_out': 'isTransferredOut',    // 当日转出
            'inDelivery': 'isInDelivery',             // 派送中
            'damaged': 'isDamaged',                   // 失败未回仓
            'allocatedParcels': 'isAllocatedParcels',  // 线路应派总量
            'pickedUpFromWarehouse': 'isPickedUpFromWarehouse',  // 司机实领
            'warehouseLost': 'isWarehouseLost',      // 仓内丢失
            'returned': 'isReturned',                 // 失败已回仓
            'unclaimed': 'isUnclaimed',              // 未领取
            'delivered': 'isDelivered',               // 已妥投（包含准时/超时/无Ascan）
            'no_ascan_delivery': 'noAscanDelivery'    // 无Ascan妥投
        };
        
        

        const recordName = 'area' in record 
            ? record.area 
            : ('driverName' in record && record.driverName) 
                ? record.driverName 
                : `Driver-${record.driverId}`;
        
        // 获取对应类型的标签名称
        const typeLabel = typeMap[type] || type;
        
        // Daily 页面：使用记录名称
        const titlePrefix = recordName;
        setDrawerTitle(`${titlePrefix} - ${typeLabel} - ${t('wms.delivery.dailyTaskMonitoring.detailDrawer.detailPage')}`);
        
        // 保存当前详情类型和记录
        const detailType = 'driverName' in record ? 'driver' : 'area';
        setCurrentDetailType(detailType);
        setCurrentDetailRecord(record);
        
        // 传递 detailType 给 getColumnsByType，避免依赖异步状态更新
        const columns = getColumnsByType(type, detailType);
        setDrawerColumns(columns);
        
        
        let contextFilters = { ...filters };
        if (filters.selectedDsps.length === 0 && 'dsp' in record && record.dsp) {
            contextFilters.selectedDsps = [record.dsp];
        }
        if (filters.selectedAreas.length === 0 && 'area' in record && record.area) {
            contextFilters.selectedAreas = [record.area];
        }
        
        // 司机维度应派件量下钻：分配+转入；其他保持单 statusField
        const isDriverAllocated = 'driverName' in record && type === 'allocatedParcels';
        const statusFieldForContext: string | undefined = isDriverAllocated ? undefined : deliveryEfficiencyStatusMap[type];
        const statusFieldsForContext: string[] | undefined = isDriverAllocated ? ['isAllocatedParcels', 'isTransferredIn'] : undefined;

        // 保存原始详情上下文，包含当前的筛选条件和 statusField/statusFields
        setLastDetailContext({ type, record, filters: contextFilters, statusField: statusFieldForContext, statusFields: statusFieldsForContext });
        // 初始化抽屉状态筛选：司机维度应派件量 = 分配+转入；其他为当前下钻列
        const initialDetailStatusTypesForRow = isDriverAllocated
            ? ['allocatedParcels', 'transferred_in']
            : (statusTypeOptions.some(opt => opt.value === type) ? [type] : []);
        setDetailStatusTypes(initialDetailStatusTypesForRow);
        
        // Daily 页面（司机/区域维度）：全部筛选都保留
        const initialDetailFilters = (() => {
            let initialDsps: string[] = [];
            if (isDsp && currentDsp && currentDsp.name) {
                initialDsps = [currentDsp.name];
            } else if ('dsp' in record && record.dsp) {
                initialDsps = [record.dsp];
            }
            return {
                selectedWarehouses: [],
                selectedDsps: initialDsps,
                selectedAreas: 'area' in record && record.area ? [record.area] : [],
                selectedDrivers: 'driverId' in record && record.driverId ? [String(record.driverId)] : []
            };
        })();
        // 标记跳过 useEffect 的自动查询，避免重复请求
        skipDetailFiltersEffectRef.current = true;
        setDetailFilters(initialDetailFilters);
        
        // 先显示抽屉，避免第二次点击不显示的问题
        setDrawerVisible(true);
        
        // 根据记录类型调用相应的查询 - 默认按状态排序
        const pageRequest = {
            page: 1,
            size: 10,
            sortBy: detailSortConfig.sortBy,
            sortDirection: detailSortConfig.sortDirection
        };
        detailRequestIdRef.current += 1;
        const openDrawerReqId = detailRequestIdRef.current;
        try {
            if ('driverName' in record) {
                // 司机维度 - 传递当前行的 dspId（司机维度不可能是hub视角，不需要判断）
                // 司机 ID 只能取行数据：drivers 选项是按关键词搜索的有限结果，表格行内司机通常不在其中
                const currentDriverId = 'driverId' in record && record.driverId ? parseInt(record.driverId.toString()) : undefined;
                if (currentDriverId) {
                    // 获取当前行的 dspId（司机维度表格中数值下钻必须传当前行的 dspId）
                    // 优先使用 record.dspId，如果没有则从 dsps 列表中根据 dsp 名称查找
                    let rowDspId = (record as DriverTaskData).dspId;
                    if (!rowDspId || rowDspId <= 0) {
                        // 如果 dspId 不存在，尝试从 dsp 名称查找
                        const dspName = (record as DriverTaskData).dsp;
                        if (dspName) {
                            const dspFromOptions = dsps.find((d: { id: number; name: string }) => d.name === dspName);
                            rowDspId = dspFromOptions?.id;
                        }
                    }
                    const dspIdsForQuery = rowDspId && rowDspId > 0 ? [rowDspId] : undefined;

                    // 获取状态字段：司机维度应派件量 = 分配+转入
                    const statusField: string | undefined = deliveryEfficiencyStatusMap[type];
                    const statusFieldsForDriver = type === 'allocatedParcels'
                        ? ['isAllocatedParcels', 'isTransferredIn']
                        : (statusField ? [statusField] : undefined);

                    // Daily 页面司机维度：传递当前行的 dspId + driverId + 树状节点的 warehouseId
                    await fetchDetailsByStatusFieldForDriver(
                        filters.taskDate.format('YYYY-MM-DD'),
                        statusFieldsForDriver,
                        pageRequest,
                        warehouseId,
                        undefined,
                        dspIdsForQuery,
                        undefined,
                        [currentDriverId],
                        undefined,
                        openDrawerReqId
                    );
                } else {
                    // 如果找不到司机ID，直接显示空抽屉
                    setDrawerData([]);
                    setDrawerVisible(true);
                }
            } else {
                // 区域维度 - 直接使用 record 中的 areaId 和 dspId（与 SLA 页面保持一致）
                const areaId = Number((record as any).areaId) || 0;
                // Hub DSP 维度下钻：必须使用“行上的 dspId”（你提到的 rowDspId 语义）
                const rowDspId = Number((record as any).dspId) || 0;
                const rowWarehouseId = Number((record as any).warehouseId) || 0;

                // Hub 视角：filters.viewMode 的语义是 “DSP视角/ WSH视角”，不是“司机维度/区域维度”
                // 支持新值：'dsp' 和 'whs'，也支持旧值：'driver' 和 'area'

                // Daily 页面区域维度下钻：传递当前行的 dspId + areaId + 树状节点的 warehouseId
                const warehouseIdForQuery = warehouseId; // Daily 页面区域维度：传递树状节点的 warehouseId

                // Daily 页面区域维度：传当前行的 dspId
                const dspIdsForQuery = rowDspId > 0 ? [rowDspId] : undefined;

                // Daily 页面区域维度：传当前行的 areaId
                const areaIdsForQuery = areaId > 0 ? [areaId] : undefined;

                // Daily 页面区域维度：需要 areaId 和 dspId
                const canQueryByDimension = areaId > 0 && rowDspId > 0;

                if (canQueryByDimension) {
                    // 获取状态字段
                    const statusField: string | undefined = deliveryEfficiencyStatusMap[type];
                    
                    // 区域维度：使用区域维度接口
                    await fetchDetailsByStatusField(
                        filters.taskDate.format('YYYY-MM-DD'),
                        statusField ? [statusField] : undefined,
                        pageRequest,
                        warehouseIdForQuery,
                        undefined,
                        undefined,
                        dspIdsForQuery,
                        areaIdsForQuery,
                        undefined,
                        undefined,
                        openDrawerReqId
                    );
                } else {
                    // 维度关键字段缺失，直接显示空抽屉
                    setDrawerData([]);
                    setDrawerVisible(true);
                }
            }
        } catch (error) {
            console.error('获取详情数据失败:', error);
            // 出错时也显示抽屉，但数据为空
            setDrawerData([]);
            setDrawerVisible(true);
        }
    };

    // 未妥投分布点击下钻（不依赖具体某一行记录；按当前 viewMode + 筛选条件查询详情）
    const handleUndeliveredDistributionClick = async (
        type: 'undelivered' | 'unclaimed' | 'inDelivery' | 'returned' | 'damaged' | 'warehouseLost'
    ) => {
        const typeLabelMap: Record<string, string> = {
            undelivered: t('wms.delivery.dailyTaskMonitoring.undeliveredDistribution.undelivered'),
            unclaimed: t('wms.delivery.dailyTaskMonitoring.tableColumns.unpicked'),
            inDelivery: t('wms.delivery.dailyTaskMonitoring.tableColumns.inDelivery'),
            returned: t('wms.delivery.dailyTaskMonitoring.tableColumns.failedReturned'),
            damaged: t('wms.delivery.dailyTaskMonitoring.tableColumns.failedNotReturned'),
            warehouseLost: t('wms.delivery.dailyTaskMonitoring.tableColumns.warehouseLost')
        };
        const label = typeLabelMap[type] || type;

        if (filters.selectedDsps.length > 1 || filters.selectedAreas.length > 1 || filters.selectedCities.length > 1 || filters.selectedDrivers.length > 1) {
            setDrawerTitle(`${label} - ${t('wms.delivery.dailyTaskMonitoring.detailDrawer.detailPage')} (${t('wms.delivery.dailyTaskMonitoring.detailDrawer.multipleFilters')})`);
        } else {
            setDrawerTitle(`${label} - ${t('wms.delivery.dailyTaskMonitoring.detailDrawer.detailPage')}`);
        }

        // Daily 页面：'driver'/'area' -> 'driver'/'area'
        const detailType: 'driver' | 'area' = filters.viewMode === 'driver' ? 'driver' : 'area';
        setCurrentDetailType(detailType);

        const virtualRecord: DriverTaskData | AreaTaskData = detailType === 'driver'
            ? {
                id: 0,
                driverId: '',
                driverName: '',
                dsp: '',
                warehouseName: currentWarehouse?.name || undefined,
                allocatedParcels: 0,
                pickedUpFromWarehouse: 0,
                warehouseLost: 0,
                inDelivery: 0,
                onTimeDelivery: 0,
                overdueDelivery: 0,
                deliveredCount: 0,
                unclaimedCount: 0,
                noAscanDelivery: 0,
                transferredIn: 0,
                transferredOut: 0,
                returned: 0,
                damaged: 0,
                deliveryRate: 0,
                failedDeliveryRate: 0
            }
            : {
                id: 0,
                area: '',
                dsp: '',
                warehouseName: currentWarehouse?.name || undefined,
                allocatedParcels: 0,
                pickedUpFromWarehouse: 0,
                warehouseLost: 0,
                inDelivery: 0,
                onTimeDelivery: 0,
                overdueDelivery: 0,
                deliveredCount: 0,
                unclaimedCount: 0,
                noAscanDelivery: 0,
                transferredIn: 0,
                transferredOut: 0,
                returned: 0,
                damaged: 0,
                deliveryRate: 0,
                failedDeliveryRate: 0
            };

        setCurrentDetailRecord(virtualRecord);
        const statusField = type === 'undelivered' ? 'isUndelivered' : getStatusField(type, t);
        
        // 如果是未妥投类型，初始化状态筛选为包含5个状态：未领取、派送中、派送失败已回仓、派送失败未回仓、仓内疑似丢失
        if (type === 'undelivered') {
            setDetailStatusTypes(['unclaimed', 'inDelivery', 'returned', 'damaged', 'warehouseLost']);
            // 更新 lastDetailContext，使用 statusFields 而不是 statusField
            const undeliveredStatusFields = ['isUnclaimed', 'isInDelivery', 'isReturned', 'isDamaged', 'isWarehouseLost'];
            setLastDetailContext({ type, record: virtualRecord, filters: { ...filters }, statusField: undefined, statusFields: undeliveredStatusFields });
        } else {
            setDetailStatusTypes(statusTypeOptions.some(opt => opt.value === type) ? [type] : []);
            setLastDetailContext({ type, record: virtualRecord, filters: { ...filters }, statusField });
        }

        const initialDetailFilters = {
            selectedWarehouses: [],
            selectedDsps: isDsp && currentDsp?.name ? [currentDsp.name] : filters.selectedDsps,
            selectedAreas: filters.selectedAreas,
            selectedDrivers: filters.selectedDrivers
        };
        skipDetailFiltersEffectRef.current = true;
        setDetailFilters(initialDetailFilters);

        setDrawerColumns(getColumnsByType(type, detailType));
        setDrawerVisible(true);

        const pageRequest = {
            page: 1,
            size: 10,
            sortBy: detailSortConfig.sortBy,
            sortDirection: detailSortConfig.sortDirection
        };

        try {
            // 未妥投分布下钻参数设置：
            // Daily 页面（司机和区域维度）：传递树状节点的 warehouseId
            const warehouseIdForQuery = warehouseId;
            
            // 如果是未妥投类型，使用状态筛选对应的 statusFields
            let statusFieldsForQuery: string[] | undefined;
            if (type === 'undelivered') {
                // 未妥投包含5个状态：未领取、派送中、派送失败已回仓、派送失败未回仓、仓内疑似丢失
                statusFieldsForQuery = ['isUnclaimed', 'isInDelivery', 'isReturned', 'isDamaged', 'isWarehouseLost'];
            } else {
                statusFieldsForQuery = statusField ? [statusField] : undefined;
            }
            
            // 将 detailFilters 中的筛选条件转换为ID（与 refetchDetailData 逻辑保持一致）
            const selectedDspIds = initialDetailFilters.selectedDsps.length > 0
                ? (() => {
                    const ids = initialDetailFilters.selectedDsps.map(dspName => {
                        const dsp = dsps.find((d: any) => d.name === dspName);
                        return dsp ? dsp.id : null;
                    }).filter(id => id !== null) as number[];
                    return ids.length > 0 ? ids : undefined;
                })()
                : undefined;
            
            const selectedAreaIds = initialDetailFilters.selectedAreas.length > 0
                ? (() => {
                    const ids = initialDetailFilters.selectedAreas.map(areaName => {
                        const area = areas.find((a: any) => a.name === areaName);
                        return area ? area.id : null;
                    }).filter(id => id !== null) as number[];
                    return ids.length > 0 ? ids : undefined;
                })()
                : undefined;
            
            const selectedDriverIds = toDriverIds(initialDetailFilters.selectedDrivers);
            
            detailRequestIdRef.current += 1;
            const undeliveredReqId = detailRequestIdRef.current;
            if (detailType === 'driver') {
                await fetchDetailsByStatusFieldForDriver(
                    filters.taskDate.format('YYYY-MM-DD'),
                    statusFieldsForQuery,
                    pageRequest,
                    warehouseIdForQuery,
                    undefined,
                    selectedDspIds,
                    selectedAreaIds,
                    selectedDriverIds,
                    undefined,
                    undeliveredReqId
                );
            } else {
                await fetchDetailsByStatusField(
                    filters.taskDate.format('YYYY-MM-DD'),
                    statusFieldsForQuery,
                    pageRequest,
                    warehouseIdForQuery,
                    undefined,
                    undefined,
                    selectedDspIds,
                    selectedAreaIds,
                    selectedDriverIds,
                    undefined,
                    undeliveredReqId
                );
            }
        } catch (error) {
            console.error('未妥投分布下钻获取详情失败:', error);
            setDrawerData([]);
            setDrawerVisible(true);
        }
    };

    // 当日派送进展下钻（应派件量、实际领取、已妥投）：按当前 viewMode + 筛选条件查询详情，不依赖具体某一行
    // 司机维度应派件量 = 分配+转入；区域维度应派件量 = 仅分配
    const handleDeliveryProgressClick = async (
        type: 'allocatedParcels' | 'pickedUpFromWarehouse' | 'delivered'
    ) => {
        const typeLabelMap: Record<string, string> = {
            allocatedParcels: t('wms.delivery.dailyTaskMonitoring.tableColumns.scheduledParcels'),
            pickedUpFromWarehouse: t('wms.delivery.dailyTaskMonitoring.summary.actualPickedUp'),
            delivered: t('wms.delivery.dailyTaskMonitoring.summary.deliveredParcels')
        };
        const statusFieldMap: Record<string, string> = {
            allocatedParcels: 'isAllocatedParcels',
            pickedUpFromWarehouse: 'isPickedUpFromWarehouse',
            delivered: 'isDelivered'
        };
        const label = typeLabelMap[type] || type;
        const detailType: 'driver' | 'area' = filters.viewMode === 'driver' ? 'driver' : 'area';
        // 司机维度应派件量下钻：分配+转入（OR）；区域维度保持仅分配
        const statusField = statusFieldMap[type];
        const statusFieldsForDriverAllocated = (type === 'allocatedParcels' && detailType === 'driver')
            ? ['isAllocatedParcels', 'isTransferredIn'] as string[]
            : undefined;
        const effectiveStatusField = statusFieldsForDriverAllocated ? undefined : statusField;
        const effectiveStatusFields = statusFieldsForDriverAllocated ?? (statusField ? [statusField] : undefined);

        if (filters.selectedDsps.length > 1 || filters.selectedAreas.length > 1 || filters.selectedCities.length > 1 || filters.selectedDrivers.length > 1) {
            setDrawerTitle(`${label} - ${t('wms.delivery.dailyTaskMonitoring.detailDrawer.detailPage')} (${t('wms.delivery.dailyTaskMonitoring.detailDrawer.multipleFilters')})`);
        } else {
            setDrawerTitle(`${label} - ${t('wms.delivery.dailyTaskMonitoring.detailDrawer.detailPage')}`);
        }

        setCurrentDetailType(detailType);

        const virtualRecord: DriverTaskData | AreaTaskData = detailType === 'driver'
            ? {
                id: 0,
                driverId: '',
                driverName: '',
                dsp: '',
                warehouseName: currentWarehouse?.name || undefined,
                allocatedParcels: 0,
                pickedUpFromWarehouse: 0,
                warehouseLost: 0,
                inDelivery: 0,
                onTimeDelivery: 0,
                overdueDelivery: 0,
                deliveredCount: 0,
                unclaimedCount: 0,
                noAscanDelivery: 0,
                transferredIn: 0,
                transferredOut: 0,
                returned: 0,
                damaged: 0,
                deliveryRate: 0,
                failedDeliveryRate: 0
            }
            : {
                id: 0,
                area: '',
                dsp: '',
                warehouseName: currentWarehouse?.name || undefined,
                allocatedParcels: 0,
                pickedUpFromWarehouse: 0,
                warehouseLost: 0,
                inDelivery: 0,
                onTimeDelivery: 0,
                overdueDelivery: 0,
                deliveredCount: 0,
                unclaimedCount: 0,
                noAscanDelivery: 0,
                transferredIn: 0,
                transferredOut: 0,
                returned: 0,
                damaged: 0,
                deliveryRate: 0,
                failedDeliveryRate: 0
            };

        setCurrentDetailRecord(virtualRecord);
        // 司机维度应派件量：抽屉状态筛选默认勾选 分配+转入
        const initialDetailStatusTypes = (detailType === 'driver' && type === 'allocatedParcels')
            ? ['allocatedParcels', 'transferred_in']
            : (statusTypeOptions.some(opt => opt.value === type) ? [type] : []);
        setDetailStatusTypes(initialDetailStatusTypes);
        setLastDetailContext({
            type,
            record: virtualRecord,
            filters: { ...filters },
            statusField: effectiveStatusField,
            statusFields: effectiveStatusFields
        });

        const initialDetailFilters = {
            selectedWarehouses: [],
            selectedDsps: isDsp && currentDsp?.name ? [currentDsp.name] : filters.selectedDsps,
            selectedAreas: filters.selectedAreas,
            selectedDrivers: filters.selectedDrivers
        };
        skipDetailFiltersEffectRef.current = true;
        setDetailFilters(initialDetailFilters);

        setDrawerColumns(getColumnsByType(type, detailType));
        setDrawerVisible(true);

        const pageRequest = {
            page: 1,
            size: 10,
            sortBy: detailSortConfig.sortBy,
            sortDirection: detailSortConfig.sortDirection
        };

        try {
            const warehouseIdForQuery = warehouseId;
            const selectedDspIds = initialDetailFilters.selectedDsps.length > 0
                ? (() => {
                    const ids = initialDetailFilters.selectedDsps.map(dspName => {
                        const dsp = dsps.find((d: any) => d.name === dspName);
                        return dsp ? dsp.id : null;
                    }).filter(id => id !== null) as number[];
                    return ids.length > 0 ? ids : undefined;
                })()
                : undefined;
            const selectedAreaIds = initialDetailFilters.selectedAreas.length > 0
                ? (() => {
                    const ids = initialDetailFilters.selectedAreas.map(areaName => {
                        const area = areas.find((a: any) => a.name === areaName);
                        return area ? area.id : null;
                    }).filter(id => id !== null) as number[];
                    return ids.length > 0 ? ids : undefined;
                })()
                : undefined;
            const selectedDriverIds = toDriverIds(initialDetailFilters.selectedDrivers);

            detailRequestIdRef.current += 1;
            const deliveryProgressReqId = detailRequestIdRef.current;
            if (detailType === 'driver') {
                await fetchDetailsByStatusFieldForDriver(
                    filters.taskDate.format('YYYY-MM-DD'),
                    effectiveStatusFields,
                    pageRequest,
                    warehouseIdForQuery,
                    undefined,
                    selectedDspIds,
                    selectedAreaIds,
                    selectedDriverIds,
                    undefined,
                    deliveryProgressReqId
                );
            } else {
                await fetchDetailsByStatusField(
                    filters.taskDate.format('YYYY-MM-DD'),
                    effectiveStatusFields,
                    pageRequest,
                    warehouseIdForQuery,
                    undefined,
                    undefined,
                    selectedDspIds,
                    selectedAreaIds,
                    selectedDriverIds,
                    undefined,
                    deliveryProgressReqId
                );
            }
        } catch (error) {
            console.error('当日派送进展下钻获取详情失败:', error);
            setDrawerData([]);
            setDrawerVisible(true);
        }
    };

    // 配送效率状态映射 - 对应数据库字段名（与handleDetailClick中的定义保持一致）
    const deliveryEfficiencyStatusMap: { [key: string]: string } = {
        'on_time_delivery': 'isOnTimeDelivery',    // 准时妥投
        'overdue_delivery': 'isOverdueDelivery',   // 超时妥投
        'transferred_in': 'isTransferredIn',       // 当日转入
        'transferred_out': 'isTransferredOut',     // 当日转出
        'inDelivery': 'isInDelivery',              // 派送中
        'damaged': 'isDamaged',                    // 失败未回仓
        'allocatedParcels': 'isAllocatedParcels',  // 线路应派总量
        'pickedUpFromWarehouse': 'isPickedUpFromWarehouse',  // 司机实领
        'warehouseLost': 'isWarehouseLost',        // 仓内丢失
        'returned': 'isReturned',                   // 失败已回仓
        'unclaimed': 'isUnclaimed',              // 未领取
        'no_ascan_delivery': 'noAscanDelivery'      // 无Ascan妥投
    };

    // 根据详情筛选条件重新查询数据
    const refetchDetailData = async () => {
        if (!lastDetailContext) return;

        const { type, record } = lastDetailContext;
        
        // 将详情筛选条件转换为ID
        // DSP/区域/司机下拉的选项统一来自主库远程搜索，此处只做名称→ID 换算
        const detailDsps = dsps;
        const selectedDspIds = detailFilters.selectedDsps.length > 0
            ? (() => {
                const ids = detailFilters.selectedDsps.map(dspName => {
                    const dsp = detailDsps.find((d: any) => d.name === dspName);
                    return dsp ? dsp.id : null;
                }).filter(id => id !== null) as number[];
                // 如果转换后是空数组，返回 undefined
                return ids.length > 0 ? ids : undefined;
            })()
            : undefined;

        const selectedAreaIds = detailFilters.selectedAreas.length > 0
            ? (() => {
                const ids = detailFilters.selectedAreas.map(areaName => {
                    const area = areas.find((a: any) => a.name === areaName);
                    return area ? area.id : null;
                }).filter(id => id !== null) as number[];
                // 如果转换后是空数组，返回 undefined
                return ids.length > 0 ? ids : undefined;
            })()
            : undefined;

        const selectedDriverIds = toDriverIds(detailFilters.selectedDrivers);

        // 构建分页请求
        const pageRequest = {
            page: detailPagination?.page || 1,
            size: detailPagination?.size || 10,
            sortBy: detailSortConfig.sortBy,
            sortDirection: detailSortConfig.sortDirection
        };

        try {
            // 根据 detailStatusTypes 获取状态字段（如果用户删掉了状态筛选，detailStatusTypes 为空数组，则查询所有状态）
            const statusFields: string[] | undefined = detailStatusTypes.length > 0
                ? detailStatusTypes.map((t) => {
                    const deliveryEfficiencyStatusMap: { [key: string]: string } = {
                        'on_time_delivery': 'isOnTimeDelivery',
                        'overdue_delivery': 'isOverdueDelivery',
                        'transferred_in': 'isTransferredIn',
                        'transferred_out': 'isTransferredOut',
                        'allocatedParcels': 'isAllocatedParcels',
                        'pickedUpFromWarehouse': 'isPickedUpFromWarehouse',
                        'delivered': 'isDelivered',
                        'unclaimed': 'isUnclaimed',
                        'inDelivery': 'isInDelivery',
                        'returned': 'isReturned',
                        'damaged': 'isDamaged',
                        'warehouseLost': 'isWarehouseLost'
                    };
                    return deliveryEfficiencyStatusMap[t];
                }).filter((v): v is string => typeof v === 'string' && v.length > 0)
                : undefined; // 如果 detailStatusTypes 为空，传递 undefined 查询所有状态

            detailRequestIdRef.current += 1;
            const refetchReqId = detailRequestIdRef.current;
            // Daily 页面使用 isDriverDimension()，Hub 页面不会进入这个分支（Hub 页面使用不同的逻辑）
            if (isDriverDimension()) {
                // 司机维度
                // DSP/区域/司机下拉的选项统一来自主库远程搜索，此处只做名称→ID 换算
                const detailAreas = areas;
                const selectedAreaIds = detailFilters.selectedAreas.length > 0
                    ? (() => {
                        const ids = detailFilters.selectedAreas.map(areaName => {
                            const area = detailAreas.find((a: any) => a.name === areaName);
                            return area ? area.id : null;
                        }).filter(id => id !== null) as number[];
                        // 如果转换后是空数组，返回 undefined
                        return ids.length > 0 ? ids : undefined;
                    })()
                    : undefined;
                
                await fetchDetailsByStatusFieldForDriver(
                    filters.taskDate.format('YYYY-MM-DD'),
                    statusFields,
                    pageRequest,
                    warehouseId,
                    undefined,
                    selectedDspIds,
                    selectedAreaIds,
                    selectedDriverIds,
                    undefined,
                    refetchReqId
                );
                    } else {
                        await fetchDetailsByStatusField(
                            filters.taskDate.format('YYYY-MM-DD'),
                            statusFields,
                            pageRequest,
                            warehouseId,
                            undefined,
                            undefined,
                            selectedDspIds,
                            selectedAreaIds,
                            selectedDriverIds,
                            undefined,
                            refetchReqId
                        );
                    }
        } catch (error) {
            console.error('重新查询详情数据失败:', error);
        }
    };

    // 转入只在司机维度可用：区域维度列表根本没有转入列（见 TaskMonitoringTable 的 getAreaColumns），
    // 在区域维度筛它筛出的结果没有任何列能对应上，是个纯误导项。
    const statusTypeOptions = useMemo(() => {
        const driverOnly = filters.viewMode === 'driver'
            ? [{ value: 'transferred_in', label: t('wms.delivery.dailyTaskMonitoring.areaColumns.transferInToday') }]
            : [];
        return [
            { value: 'allocatedParcels', label: t('wms.delivery.dailyTaskMonitoring.areaColumns.allocatedParcels') },
            ...driverOnly,
            { value: 'pickedUpFromWarehouse', label: t('wms.delivery.dailyTaskMonitoring.summary.actualPickedUp') },
            { value: 'delivered', label: t('wms.delivery.dailyTaskMonitoring.tableColumns.delivered') },
            { value: 'unclaimed', label: t('wms.delivery.dailyTaskMonitoring.tableColumns.unpicked') },
            { value: 'inDelivery', label: t('wms.delivery.dailyTaskMonitoring.tableColumns.inDelivery') },
            { value: 'returned', label: t('wms.delivery.dailyTaskMonitoring.tableColumns.failedReturned') },
            { value: 'damaged', label: t('wms.delivery.dailyTaskMonitoring.tableColumns.failedNotReturned') },
            { value: 'warehouseLost', label: t('wms.delivery.dailyTaskMonitoring.tableColumns.warehouseLost') }
        ];
    }, [t, filters.viewMode]);

    const handleDetailStatusTypeChange = useCallback(async (nextTypes: string[]) => {
        if (!drawerVisible || !lastDetailContext) return;

        const nextTypesSafe = nextTypes || [];
        setDetailStatusTypes(nextTypesSafe);
        const statusFields = nextTypesSafe
            .map((t) => deliveryEfficiencyStatusMap[t])
            .filter((v): v is string => typeof v === 'string' && v.length > 0);

        // 更新抽屉标题（沿用“司机/区域”下钻命名方式）
        const labelMap: Record<string, string> = {
            allocatedParcels: t('wms.delivery.dailyTaskMonitoring.areaColumns.allocatedParcels'),
            transferred_in: t('wms.delivery.dailyTaskMonitoring.areaColumns.transferInToday'),
            pickedUpFromWarehouse: t('wms.delivery.dailyTaskMonitoring.summary.actualPickedUp'),
            delivered: t('wms.delivery.dailyTaskMonitoring.tableColumns.delivered'),
            unclaimed: t('wms.delivery.dailyTaskMonitoring.tableColumns.unpicked'),
            inDelivery: t('wms.delivery.dailyTaskMonitoring.tableColumns.inDelivery'),
            returned: t('wms.delivery.dailyTaskMonitoring.tableColumns.failedReturned'),
            damaged: t('wms.delivery.dailyTaskMonitoring.tableColumns.failedNotReturned'),
            warehouseLost: t('wms.delivery.dailyTaskMonitoring.tableColumns.warehouseLost')
        };
        const nextLabel = nextTypesSafe.length === 1 ? (labelMap[nextTypesSafe[0]] || nextTypesSafe[0]) : t('wms.delivery.dailyTaskMonitoring.detailColumns.status');

        const record = lastDetailContext.record as any;
        // Daily 页面：根据筛选条件决定标题前缀
        const titlePrefix = (() => {
            // 如果用户选择了 dsp，显示 dsp 名称；如果清空，显示 record 中的 dsp
            if (detailFilters.selectedDsps && detailFilters.selectedDsps.length > 0) {
                return detailFilters.selectedDsps[0];
            }
            // 如果用户选择了 area，显示 area 名称；如果清空，显示 record 中的 area
            if (detailFilters.selectedAreas && detailFilters.selectedAreas.length > 0) {
                return detailFilters.selectedAreas[0];
            }
            // 如果用户选择了 driver，显示 driver 名称；如果清空或姓名未缓存，显示 record 中的 driverName
            const selectedDriverName = detailFilters.selectedDrivers && detailFilters.selectedDrivers.length > 0
                ? driverNameById.current.get(Number(detailFilters.selectedDrivers[0]))
                : undefined;
            if (selectedDriverName) {
                return selectedDriverName;
            }
            // 如果都清空了，使用 record 中的信息
            return record.area
                ? record.area
                : record.driverName
                    ? record.driverName
                    : '';
        })();

        setDrawerTitle(titlePrefix
            ? `${titlePrefix} - ${nextLabel} - ${t('wms.delivery.dailyTaskMonitoring.detailDrawer.detailPage')}`
            : `${nextLabel} - ${t('wms.delivery.dailyTaskMonitoring.detailDrawer.detailPage')}`);

        // 更新列（只展示当前状态对应的列）
        const detailType = currentDetailType === 'driver' ? 'driver' : 'area';
        setDrawerColumns(nextTypesSafe.length === 1 ? getColumnsByType(nextTypesSafe[0], detailType) : getColumnsByType('status', detailType));

        // 更新上下文（用于导出/分页/排序）
        setLastDetailContext((prev) => prev ? ({
            ...prev,
            // 多选时不改变 type（避免误导）；单选时更新 type
            ...(nextTypesSafe.length === 1 ? { type: nextTypesSafe[0] } : {}),
            // 单选兼容旧字段；多选走 statusFields
            statusField: nextTypesSafe.length === 1 ? deliveryEfficiencyStatusMap[nextTypesSafe[0]] : undefined,
            statusFields: statusFields
        }) : prev);

        // 切换状态时回到第一页重新查（复用 refetchDetailData 的逻辑）
        // 这里直接调用查询，避免等待 setState 导致的时序问题
        // DSP/区域/司机下拉的选项统一来自主库远程搜索，此处只做名称→ID 换算
        const detailDsps = dsps;
        const selectedDspIds = detailFilters.selectedDsps.length > 0
            ? (() => {
                const ids = detailFilters.selectedDsps.map(dspName => {
                    const dsp = detailDsps.find((d: any) => d.name === dspName);
                    return dsp ? dsp.id : null;
                }).filter(id => id !== null) as number[];
                // 如果转换后是空数组，返回 undefined
                return ids.length > 0 ? ids : undefined;
            })()
            : undefined;

        const detailAreas = areas;
        const selectedAreaIds = detailFilters.selectedAreas.length > 0
            ? (() => {
                const ids = detailFilters.selectedAreas.map(areaName => {
                    const area = detailAreas.find((a: any) => a.name === areaName);
                    return area ? area.id : null;
                }).filter(id => id !== null) as number[];
                return ids.length > 0 ? ids : undefined;
            })()
            : undefined;

        const selectedDriverIds = toDriverIds(detailFilters.selectedDrivers);

        const pageRequest = {
            page: 1,
            size: detailPagination?.size || 10,
            sortBy: detailSortConfig.sortBy,
            sortDirection: detailSortConfig.sortDirection
        };

        // Daily 页面：根据 detailFilters.selectedWarehouses 决定，如果没有则使用当前 warehouseId
        const getWarehouseIdForStatusChange = (): string | number | undefined => {
            // 根据详情筛选条件中的 selectedWarehouses 来决定
            if (detailFilters.selectedWarehouses && detailFilters.selectedWarehouses.length > 0) {
                // 计算 filteredWarehouses（与返回值的逻辑一致）
                const warehousesForQuery = accessibleWarehouses.map(whs => ({ id: whs.id || '', name: whs.name || '' }));
                
                // 根据 warehouse 名称找到对应的 warehouseId
                const selectedWarehouse = warehousesForQuery.find(whs => 
                    detailFilters.selectedWarehouses?.includes(whs.name)
                );
                if (selectedWarehouse) {
                    return selectedWarehouse.id ? parseInt(selectedWarehouse.id) : undefined;
                }
            }
            
            // 如果没有选择 warehouse，则使用当前 warehouseId
            if (warehouseId) {
                return warehouseId;
            }
            
            return undefined;
        };
        
        const warehouseIdForStatusChange = getWarehouseIdForStatusChange();
        detailRequestIdRef.current += 1;
        const detailReqId = detailRequestIdRef.current;
        try {
            // Daily 页面使用 isDriverDimension()，Hub 页面不会进入这个分支（Hub 页面使用不同的逻辑）
            if (isDriverDimension()) {
                await fetchDetailsByStatusFieldForDriver(
                    filters.taskDate.format('YYYY-MM-DD'),
                    statusFields,
                    pageRequest,
                    warehouseIdForStatusChange,
                    undefined,
                    selectedDspIds,
                    selectedAreaIds,
                    selectedDriverIds,
                    undefined,
                    detailReqId
                );
            } else {
                await fetchDetailsByStatusField(
                    filters.taskDate.format('YYYY-MM-DD'),
                    statusFields,
                    pageRequest,
                    warehouseIdForStatusChange,
                    undefined,
                    undefined,
                    selectedDspIds,
                    selectedAreaIds,
                    selectedDriverIds,
                    undefined,
                    detailReqId
                );
            }
        } catch (e) {
            console.error('切换状态重新查询详情失败:', e);
        }
    }, [
        drawerVisible,
        lastDetailContext,
        t,
        currentDetailType,
        detailFilters,
        areas,
        drivers,
        dsps,
        detailPagination?.size,
        detailSortConfig.sortBy,
        detailSortConfig.sortDirection,
        filters.viewMode,
        filters.taskDate,
        warehouseId,
        accessibleWarehouses,
        fetchDetailsByStatusField,
        fetchDetailsByStatusFieldForDriver
    ]);

    // 监听详情筛选条件变化，重新查询数据
    useEffect(() => {
        // 如果标记为跳过，则重置标记并返回，不执行查询
        if (skipDetailFiltersEffectRef.current) {
            skipDetailFiltersEffectRef.current = false;
            return;
        }
        
        if (drawerVisible && lastDetailContext) {
            // 筛选条件改变时，重置到第一页并重新查询
            // 注意：这里不直接调用 refetchDetailData，而是通过修改分页状态来触发查询
            // 因为 refetchDetailData 依赖 detailPagination，需要先更新分页状态
            const pageRequest = {
                page: 1,
                size: detailPagination?.size || 10,
                sortBy: detailSortConfig.sortBy,
                sortDirection: detailSortConfig.sortDirection
            };
            
            // 将详情筛选条件转换为ID
            // DSP/区域/司机下拉的选项统一来自主库远程搜索，此处只做名称→ID 换算
            const detailDsps = dsps;
            const selectedDspIds = detailFilters.selectedDsps.length > 0
                ? (() => {
                    const ids = detailFilters.selectedDsps.map(dspName => {
                        const dsp = detailDsps.find((d: any) => d.name === dspName);
                        return dsp ? dsp.id : null;
                    }).filter(id => id !== null) as number[];
                    // 如果转换后是空数组，返回 undefined
                    return ids.length > 0 ? ids : undefined;
                })()
                : undefined;

            // DSP/区域/司机下拉的选项统一来自主库远程搜索，此处只做名称→ID 换算
            const detailAreas = areas;
            const selectedAreaIds = detailFilters.selectedAreas.length > 0
                ? (() => {
                    const ids = detailFilters.selectedAreas.map(areaName => {
                        const area = detailAreas.find((a: any) => a.name === areaName);
                        return area ? area.id : null;
                    }).filter(id => id !== null) as number[];
                    // 如果转换后是空数组，返回 undefined
                    return ids.length > 0 ? ids : undefined;
                })()
                : undefined;

            const selectedDriverIds = toDriverIds(detailFilters.selectedDrivers);

            // 根据 detailStatusTypes 获取状态字段（如果用户删掉了状态筛选，detailStatusTypes 为空数组，则查询所有状态）
            const statusFields: string[] | undefined = detailStatusTypes.length > 0
                ? detailStatusTypes.map((t) => {
                    const deliveryEfficiencyStatusMap: { [key: string]: string } = {
                        'on_time_delivery': 'isOnTimeDelivery',
                        'overdue_delivery': 'isOverdueDelivery',
                        'transferred_in': 'isTransferredIn',
                        'transferred_out': 'isTransferredOut',
                        'allocatedParcels': 'isAllocatedParcels',
                        'pickedUpFromWarehouse': 'isPickedUpFromWarehouse',
                        'delivered': 'isDelivered',
                        'unclaimed': 'isUnclaimed',
                        'inDelivery': 'isInDelivery',
                        'returned': 'isReturned',
                        'damaged': 'isDamaged',
                        'warehouseLost': 'isWarehouseLost'
                    };
                    return deliveryEfficiencyStatusMap[t];
                }).filter((v): v is string => typeof v === 'string' && v.length > 0)
                : undefined; // 如果 detailStatusTypes 为空，传递 undefined 查询所有状态

            // Daily 页面：根据 detailFilters.selectedWarehouses 决定，如果没有则使用当前 warehouseId
            const getWarehouseIdForQuery = (): string | number | undefined => {
                // 根据详情筛选条件中的 selectedWarehouses 来决定
                if (detailFilters.selectedWarehouses && detailFilters.selectedWarehouses.length > 0) {
                    // 计算 filteredWarehouses（与返回值的逻辑一致）
                    const warehousesForQuery = accessibleWarehouses.map(whs => ({ id: whs.id || '', name: whs.name || '' }));
                    
                    // 根据 warehouse 名称找到对应的 warehouseId
                    const selectedWarehouse = warehousesForQuery.find(whs => 
                        detailFilters.selectedWarehouses?.includes(whs.name)
                    );
                    if (selectedWarehouse) {
                        return selectedWarehouse.id ? parseInt(selectedWarehouse.id) : undefined;
                    }
                }
                
                // 如果没有选择 warehouse，则使用当前 warehouseId
                if (warehouseId) {
                    return warehouseId;
                }
                
                return undefined;
            };
            
            const warehouseIdForQuery = getWarehouseIdForQuery();
            
            const fetchData = async () => {
                detailRequestIdRef.current += 1;
                const filtersChangeReqId = detailRequestIdRef.current;
                try {
                    // Daily 页面使用 isDriverDimension()，Hub 页面不会进入这个分支（Hub 页面使用不同的逻辑）
                    if (isDriverDimension()) {
                        // 司机维度：使用区域筛选
                        await fetchDetailsByStatusFieldForDriver(
                            filters.taskDate.format('YYYY-MM-DD'),
                            statusFields,
                            pageRequest,
                            warehouseIdForQuery,
                            undefined,
                            selectedDspIds,
                            selectedAreaIds,
                            selectedDriverIds,
                            undefined,
                            filtersChangeReqId
                        );
                    } else {
                        await fetchDetailsByStatusField(
                            filters.taskDate.format('YYYY-MM-DD'),
                            statusFields,
                            pageRequest,
                            warehouseIdForQuery,
                            undefined,
                            undefined,
                            selectedDspIds,
                            selectedAreaIds,
                            selectedDriverIds,
                            undefined,
                            filtersChangeReqId
                        );
                    }
                } catch (error) {
                    console.error('重新查询详情数据失败:', error);
                }
            };
            
            // 更新标题：根据筛选条件更新标题前缀和状态标签
            if (lastDetailContext) {
                const { record } = lastDetailContext;
                // 使用 detailStatusTypes 来决定状态标签，而不是 lastDetailContext.type
                const labelMap: Record<string, string> = {
                    allocatedParcels: t('wms.delivery.dailyTaskMonitoring.areaColumns.allocatedParcels'),
                    transferred_in: t('wms.delivery.dailyTaskMonitoring.areaColumns.transferInToday'),
                    pickedUpFromWarehouse: t('wms.delivery.dailyTaskMonitoring.summary.actualPickedUp'),
                    delivered: t('wms.delivery.dailyTaskMonitoring.tableColumns.delivered'),
                    unclaimed: t('wms.delivery.dailyTaskMonitoring.tableColumns.unpicked'),
                    inDelivery: t('wms.delivery.dailyTaskMonitoring.tableColumns.inDelivery'),
                    returned: t('wms.delivery.dailyTaskMonitoring.tableColumns.failedReturned'),
                    damaged: t('wms.delivery.dailyTaskMonitoring.tableColumns.failedNotReturned'),
                    warehouseLost: t('wms.delivery.dailyTaskMonitoring.tableColumns.warehouseLost')
                };
                const typeLabel = detailStatusTypes.length === 1 
                    ? (labelMap[detailStatusTypes[0]] || detailStatusTypes[0]) 
                    : t('wms.delivery.dailyTaskMonitoring.detailColumns.status');
                
                // Daily 页面：根据筛选条件构建标题，格式：dsp - 区域 - 司机 - 状态
                const titleParts: string[] = [];
                
                // 1. DSP：优先使用筛选条件，否则使用 record 中的 dsp
                const dspValue = detailFilters.selectedDsps && detailFilters.selectedDsps.length > 0
                    ? detailFilters.selectedDsps[0]
                    : ('dsp' in record && record.dsp) ? record.dsp : null;
                if (dspValue) {
                    titleParts.push(dspValue);
                }
                
                // 2. 区域：优先使用筛选条件，否则使用 record 中的 area
                const areaValue = detailFilters.selectedAreas && detailFilters.selectedAreas.length > 0
                    ? detailFilters.selectedAreas[0]
                    : ('area' in record && record.area) ? record.area : null;
                if (areaValue) {
                    titleParts.push(areaValue);
                }
                
                // 3. 司机：优先使用筛选条件，否则使用 record 中的 driverName
                const selectedDriverName = detailFilters.selectedDrivers && detailFilters.selectedDrivers.length > 0
                    ? driverNameById.current.get(Number(detailFilters.selectedDrivers[0]))
                    : undefined;
                const driverValue = selectedDriverName
                    ?? (('driverName' in record && record.driverName)
                        ? record.driverName
                        : ('driverId' in record && record.driverId)
                            ? `Driver-${record.driverId}`
                            : null);
                if (driverValue) {
                    titleParts.push(driverValue);
                }
                
                // 4. 状态：使用状态标签
                if (typeLabel) {
                    titleParts.push(typeLabel);
                }
                
                // 构建最终标题：用 " - " 连接各部分，最后加上 "详情页"
                const titlePrefix = titleParts.length > 0 ? titleParts.join(' - ') : '';
                setDrawerTitle(titlePrefix
                    ? `${titlePrefix} - ${t('wms.delivery.dailyTaskMonitoring.detailDrawer.detailPage')}`
                    : `${typeLabel} - ${t('wms.delivery.dailyTaskMonitoring.detailDrawer.detailPage')}`);
            }
            
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        detailFilters,
        detailStatusTypes,
        drawerVisible,
        lastDetailContext,
        detailPagination?.size,
        detailSortConfig.sortBy,
        detailSortConfig.sortDirection,
        filters.taskDate,
        filters.viewMode,
        warehouseId
        // 注意：不包含 areas, drivers, dsps, accessibleWarehouses 等
        // 因为这些可能在每次渲染时创建新的引用，导致无限循环
        // 如果需要这些数据，在 useEffect 内部通过闭包访问即可
    ]);

    // 监听详情数据变化，更新抽屉数据（仅当响应与当前请求匹配时更新，避免竞态导致显示错误条数）
    useEffect(() => {
        if (!newDetailData) return;
        if (detailDataRequestId != null && detailDataRequestId !== detailRequestIdRef.current) return;
        const convertedData: DetailData[] = newDetailData.map(item => ({
            id: item.id ? String(item.id) : undefined,
            dsp_name: item.dspName || '',
            area_name: item.areaName || '',
            driver_id: item.driverId || 0,
            driver_name: item.driverName && item.driverName.trim().length > 0 ? item.driverName : '-',
            task_date: item.taskDate || '',
            tracking_number: item.trackingNumber,
            status: item.overallStatusText || getStatusText(item.currentStatus || 0),
            order_id: item.orderId || 0,
            last_failure_reason: item.lastFailureReason || '',
            exception_reason_code: item.exceptionReasonCode || undefined,
            latest_physical_tracking_updated_at: item.latestPhysicalTrackingUpdatedAt || ''
        }));
        setDrawerData(convertedData);
    }, [newDetailData, detailDataRequestId, currentDetailType]);

    // 使用 useRef 跟踪上一次查询的参数，避免重复查询
    const lastQueryParamsRef = React.useRef<string>('');

    // 记录最近一次列表查询实际使用的筛选条件（已解析为 ID），供汇总下载复用，保证「与页面一模一样」
    const currentSummaryDownloadArgsRef = React.useRef<{
        taskDate: string;
        viewMode: string;
        warehouseId?: number;
        warehouseIds?: number[];
        dspIds?: number[];
        areaIds?: number[];
        driverIds?: number[];
        cityNames?: string[];
        sortBy?: string;
        sortDirection?: 'ASC' | 'DESC';
    } | null>(null);

    // 初始化查询和分页查询（包含筛选条件）
    useEffect(() => {
        // DSP 已选名称到 id 的映射来自累积缓存（见 dspIdByName）。
        // 名称只能从下拉选中，选中时必然已写入缓存；若出现未命中说明数据未就绪，
        // 此时先不查询，避免带着不完整的筛选条件请求、导致结果静默错误。
        // 司机不需要这道门禁：下拉 value 本身就是 accountId，不存在未映射态。
        const hasUnmappedSelection =
            filters.selectedDsps.some((name: string) => !dspIdByName.current.has(name));
        if (hasUnmappedSelection) {
            return;
        }

        const computedSelectedDspIds = filters.selectedDsps.length > 0
            ? filters.selectedDsps
                .map((dspName: string) => dspIdByName.current.get(dspName) ?? null)
                .filter((id: number | null): id is number => id !== null)
            : undefined;

        const computedSelectedDriverIds = toDriverIds(filters.selectedDrivers);
        
        // 如果是DSP经理，自动添加DSP过滤条件
        const finalDspIds = isDsp && dspId 
            ? [parseInt(dspId)] 
            : (computedSelectedDspIds && computedSelectedDspIds.length > 0 ? computedSelectedDspIds : undefined);
        
        const selectedAreaIds = filters.selectedAreas.length > 0 
            ? convertNamesToIds(filters.selectedAreas, areas)
            : undefined;

        const selectedWarehouseIdsForQuery = (filters.selectedWarehouses && filters.selectedWarehouses.length > 0)
            ? filters.selectedWarehouses
                .map((id: string) => Number(id))
                .filter((id: number) => Number.isFinite(id) && id > 0)
            : undefined;
        
        // 构建查询参数的唯一标识，用于避免重复查询
        const queryParamsKey = JSON.stringify({
            viewMode: filters.viewMode,
            taskDate: taskDateString,
            currentPage,
            pageSize,
            sortBy: sortConfig.sortBy,
            sortDirection: sortConfig.sortDirection,
            warehouseId: currentWarehouse?.id,
            selectedWarehouseIdsForQuery,
            finalDspIds,
            selectedAreaIds,
            computedSelectedDriverIds,
            selectedCities: filters.selectedCities
        });
        
        // 如果查询参数没有变化，跳过查询
        if (lastQueryParamsRef.current === queryParamsKey) {
            return;
        }
        
        // 更新查询参数标识
        lastQueryParamsRef.current = queryParamsKey;

        // 记录本次查询实际使用的筛选条件，供汇总下载复用
        const resolvedWarehouseId = Number(warehouseId);
        currentSummaryDownloadArgsRef.current = {
            taskDate: taskDateString,
            viewMode: filters.viewMode,
            warehouseId: Number.isFinite(resolvedWarehouseId) && resolvedWarehouseId > 0 ? resolvedWarehouseId : undefined,
            warehouseIds: selectedWarehouseIdsForQuery && selectedWarehouseIdsForQuery.length > 0 ? selectedWarehouseIdsForQuery : undefined,
            dspIds: finalDspIds && finalDspIds.length > 0 ? finalDspIds : undefined,
            areaIds: isAreaDimension() && selectedAreaIds && selectedAreaIds.length > 0 ? selectedAreaIds : undefined,
            driverIds: !isAreaDimension() && computedSelectedDriverIds && computedSelectedDriverIds.length > 0 ? computedSelectedDriverIds : undefined,
            cityNames: isAreaDimension() && filters.selectedCities.length > 0 ? filters.selectedCities : undefined,
            sortBy: sortConfig.sortBy,
            sortDirection: sortConfig.sortDirection,
        };

        if (isAreaDimension()) {
            // 非Hub页面：区域维度使用区域汇总查询
            const baseParams = getAreaQueryParams();
            const areaPageRequest = {
                taskDate: taskDateString,
                ...baseParams, // 使用正确的hubId或warehouseId
                warehouseIds: selectedWarehouseIdsForQuery && selectedWarehouseIdsForQuery.length > 0 ? selectedWarehouseIdsForQuery : undefined,
                dspIds: finalDspIds, // 包含筛选条件和DSP经理过滤
                areaIds: selectedAreaIds && selectedAreaIds.length > 0 ? selectedAreaIds : undefined,
                cities: filters.selectedCities.length > 0 ? filters.selectedCities : undefined,
                dimensionType: getCurrentDimensionType(),
                pageRequest: {
                    page: currentPage,
                    size: pageSize,
                    sortBy: sortConfig.sortBy || 'id',
                    sortDirection: sortConfig.sortDirection || 'ASC' as 'ASC' | 'DESC'
                }
            };

            handleAreaFilter(areaPageRequest);
        } else {
            // 非Hub页面：司机维度使用司机维度查询
            const baseParams = getAreaQueryParams();

            // 构建筛选条件
            const driverPageRequest = {
                filter: {
                    taskDate: taskDateString,
                    warehouseId: baseParams.warehouseId ? parseInt(baseParams.warehouseId.toString()) : undefined,
                    dspIds: finalDspIds && finalDspIds.length > 0 ? finalDspIds : undefined,
                    areaIds: undefined, // 司机维度不需要区域筛选
                    driverIds: computedSelectedDriverIds && computedSelectedDriverIds.length > 0 ? computedSelectedDriverIds : undefined,
                    cityIds: undefined, // 司机维度不需要城市筛选
                    cities: undefined, // 司机维度不需要城市筛选
                },
                pageRequest: {
                    page: currentPage,
                    size: pageSize,
                    sortBy: sortConfig.sortBy || 'id',
                    sortDirection: sortConfig.sortDirection || 'ASC' as 'ASC' | 'DESC'
                }
            };

            handleDriverFilter(driverPageRequest);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, pageSize, filters.viewMode, taskDateString, JSON.stringify(filters.selectedWarehouses), JSON.stringify(filters.selectedDsps), JSON.stringify(filters.selectedAreas), JSON.stringify(filters.selectedCities), JSON.stringify(filters.selectedDrivers), isDsp, dspId, sortConfig.sortBy, sortConfig.sortDirection, currentWarehouse?.id]); // 使用稳定的 taskDateString 而不是 filters.taskDate.format('YYYY-MM-DD')

    // 分页处理函数：仅更新状态，具体请求交给 useEffect 统一处理
    const handlePageChange = (page: number, newPageSize?: number) => {
        setCurrentPage(page);
        if (newPageSize && newPageSize !== pageSize) {
            setPageSize(newPageSize);
        }
    };

    // 详情分页处理函数
    const handleDetailPageChange = async (page: number, pageSize?: number) => {
        // 使用 drawerVisible 和 lastDetailContext 来判断，而不是 currentDetailRecord
        // 因为分页切换时可能 currentDetailRecord 已经被清空，但查询上下文还在
        if (!drawerVisible || !lastDetailContext) return;
        
        const pageRequest = {
            page,
            size: pageSize || 10,
            sortBy: detailSortConfig.sortBy,
            sortDirection: detailSortConfig.sortDirection
        };
        
        // Daily 页面：根据 detailFilters.selectedWarehouses 决定，如果没有则使用当前 warehouseId
        const getWarehouseIdForQuery = (): string | number | undefined => {
            // 根据详情筛选条件中的 selectedWarehouses 来决定
            if (detailFilters.selectedWarehouses && detailFilters.selectedWarehouses.length > 0) {
                // 计算 filteredWarehouses（与返回值的逻辑一致）
                const warehousesForQuery = accessibleWarehouses.map(whs => ({ id: whs.id || '', name: whs.name || '' }));
                
                // 根据 warehouse 名称找到对应的 warehouseId
                const selectedWarehouse = warehousesForQuery.find(whs => 
                    detailFilters.selectedWarehouses?.includes(whs.name)
                );
                if (selectedWarehouse) {
                    return selectedWarehouse.id ? parseInt(selectedWarehouse.id) : undefined;
                }
            }
            
            // 如果没有选择 warehouse，则使用当前 warehouseId
            if (warehouseId) {
                return warehouseId;
            }
            
            return undefined;
        };
        
        const warehouseIdForQuery = getWarehouseIdForQuery();
        
        try {
            // 区域维度和司机维度 - 使用详情筛选条件
            // DSP/区域/司机下拉的选项统一来自主库远程搜索，此处只做名称→ID 换算
            const detailDsps = dsps;
            const selectedDspIds = detailFilters.selectedDsps.length > 0
                ? (() => {
                    const ids = detailFilters.selectedDsps.map(dspName => {
                        const dsp = detailDsps.find((d: any) => d.name === dspName);
                        return dsp ? dsp.id : null;
                    }).filter(id => id !== null) as number[];
                    // 如果转换后是空数组，返回 undefined
                    return ids.length > 0 ? ids : undefined;
                })()
                : undefined;

            const selectedAreaIds = detailFilters.selectedAreas.length > 0
                ? detailFilters.selectedAreas.map(areaName => {
                    const area = areas.find((a: any) => a.name === areaName);
                    return area ? area.id : null;
                }).filter(id => id !== null) as number[]
                : undefined;

            // 详情查询不使用城市条件
            const selectedCities = undefined;

            // 根据 detailStatusTypes 获取状态字段（如果用户删掉了状态筛选，detailStatusTypes 为空数组，则查询所有状态）
            const statusFields: string[] | undefined = detailStatusTypes.length > 0
                ? detailStatusTypes.map((t) => {
                    const deliveryEfficiencyStatusMap: { [key: string]: string } = {
                        'on_time_delivery': 'isOnTimeDelivery',
                        'overdue_delivery': 'isOverdueDelivery',
                        'transferred_in': 'isTransferredIn',
                        'transferred_out': 'isTransferredOut',
                        'allocatedParcels': 'isAllocatedParcels',
                        'pickedUpFromWarehouse': 'isPickedUpFromWarehouse',
                        'delivered': 'isDelivered',
                        'unclaimed': 'isUnclaimed',
                        'inDelivery': 'isInDelivery',
                        'returned': 'isReturned',
                        'damaged': 'isDamaged',
                        'warehouseLost': 'isWarehouseLost'
                    };
                    return deliveryEfficiencyStatusMap[t];
                }).filter((v): v is string => typeof v === 'string' && v.length > 0)
                : undefined; // 如果 detailStatusTypes 为空，传递 undefined 查询所有状态

            // 如果有statusFields，应该使用fetchDetailsByStatusField查询
            detailRequestIdRef.current += 1;
            const filtersEffectReqId = detailRequestIdRef.current;
            if (statusFields && statusFields.length > 0) {
                // Daily 页面使用 isDriverDimension()，Hub 页面不会进入这个分支（Hub 页面使用不同的逻辑）
                if (isDriverDimension()) {
                    // 司机维度：使用司机维度接口 - 使用详情筛选条件
                    // DSP/区域/司机下拉的选项统一来自主库远程搜索，此处只做名称→ID 换算
                    const detailAreas = areas;
                    const selectedAreaIds = detailFilters.selectedAreas.length > 0
                        ? (() => {
                            const ids = detailFilters.selectedAreas.map(areaName => {
                                const area = detailAreas.find((a: any) => a.name === areaName);
                                return area ? area.id : null;
                            }).filter(id => id !== null) as number[];
                            // 如果转换后是空数组，返回 undefined
                            return ids.length > 0 ? ids : undefined;
                        })()
                        : undefined;
                    
                    const selectedDriverIds = toDriverIds(detailFilters.selectedDrivers);
                    
                    await fetchDetailsByStatusFieldForDriver(
                        filters.taskDate.format('YYYY-MM-DD'),
                        statusFields,
                        pageRequest,
                        warehouseId,
                        undefined,
                        selectedDspIds,
                        selectedAreaIds,
                        selectedDriverIds,
                        undefined,
                        filtersEffectReqId
                    );
                } else {
                    const selectedDriverIds = toDriverIds(detailFilters.selectedDrivers);
                    
                    await fetchDetailsByStatusField(
                        filters.taskDate.format('YYYY-MM-DD'),
                        statusFields,
                        pageRequest,
                        warehouseIdForQuery,
                        undefined,
                        undefined,
                        selectedDspIds,
                        selectedAreaIds,
                        selectedDriverIds,
                        undefined,
                        filtersEffectReqId
                    );
                }
            } else {
                // 如果 detailStatusTypes 为空，查询所有状态（不传 statusFields）
                if (isDriverDimension()) {
                    // 司机维度：使用司机维度接口
                    const detailAreas = areas;
                    const selectedAreaIds = detailFilters.selectedAreas.length > 0
                        ? (() => {
                            const ids = detailFilters.selectedAreas.map(areaName => {
                                const area = detailAreas.find((a: any) => a.name === areaName);
                                return area ? area.id : null;
                            }).filter(id => id !== null) as number[];
                            return ids.length > 0 ? ids : undefined;
                        })()
                        : undefined;
                    
                    const selectedDriverIds = toDriverIds(detailFilters.selectedDrivers);
                    
                    await fetchDetailsByStatusFieldForDriver(
                        filters.taskDate.format('YYYY-MM-DD'),
                        undefined,
                        pageRequest,
                        warehouseId,
                        undefined,
                        selectedDspIds,
                        selectedAreaIds,
                        selectedDriverIds,
                        undefined,
                        filtersEffectReqId
                    );
                } else {
                    const selectedDriverIds = toDriverIds(detailFilters.selectedDrivers);
                    
                    await fetchDetailsByStatusField(
                        filters.taskDate.format('YYYY-MM-DD'),
                        undefined,
                        pageRequest,
                        warehouseIdForQuery,
                        undefined,
                        undefined,
                        selectedDspIds,
                        selectedAreaIds,
                        selectedDriverIds,
                        undefined,
                        filtersEffectReqId
                    );
                }
            }
        } catch (error) {
            console.error('获取详情分页数据失败:', error);
        }
    };

    // 处理排序变化
    const handleSortChange = (sortBy: string, sortDirection: 'ASC' | 'DESC') => {
        // 只更新状态，让 useEffect 自动处理查询
        // 这样可以确保状态更新完成后再查询，并且与筛选条件正确结合
        setSortConfig({ sortBy, sortDirection });
        setCurrentPage(1); // 重置到第一页
        // 注意：不在这里直接调用 handleAreaFilter，让 useEffect 自动处理查询
    };

    // 处理详情排序变化
    const handleDetailSortChange = async (sortBy: string, sortDirection: 'ASC' | 'DESC') => {
        // 更新排序状态
        setDetailSortConfig({ sortBy, sortDirection });
        
        // 如果抽屉是打开的，重新获取数据
        if (drawerVisible && drawerData.length > 0 && lastDetailContext) {
            try {
                detailRequestIdRef.current += 1;
                const sortReqId = detailRequestIdRef.current;
        const pageRequest = {
                    page: 1, // 重置到第一页
            size: 10,
                    sortBy: sortBy,
                    sortDirection: sortDirection
                };

                // 使用保存的上下文数据（司机维度应派件量时 statusFields 为 ['isAllocatedParcels', 'isTransferredIn']）
                const { type, record, filters: savedFilters, statusFields: savedStatusFields } = lastDetailContext;
                
                
                
                // 根据当前详情类型重新获取数据 - 使用详情筛选条件
                if (currentDetailType === 'driver' && 'driverId' in record) {
                    // 司机维度详情排序：使用详情筛选条件
                    // DSP/区域/司机下拉的选项统一来自主库远程搜索，此处只做名称→ID 换算
                    const detailDsps = dsps;
                    const selectedDspIds = detailFilters.selectedDsps.length > 0
                        ? (() => {
                            const ids = detailFilters.selectedDsps.map(dspName => {
                                const dsp = detailDsps.find((d: any) => d.name === dspName);
                                return dsp ? dsp.id : null;
                            }).filter(id => id !== null) as number[];
                            // 如果转换后是空数组，返回 undefined
                            return ids.length > 0 ? ids : undefined;
                        })()
                        : undefined;
                    
                    const selectedAreaIds = detailFilters.selectedAreas.length > 0
                        ? detailFilters.selectedAreas.map(areaName => {
                            const area = areas.find((a: any) => a.name === areaName);
                            return area ? area.id : null;
                        }).filter(id => id !== null) as number[]
                        : undefined;
                    
                    const selectedDriverIds = toDriverIds(detailFilters.selectedDrivers);
                    
                    // 优先使用保存的 statusFields（司机维度应派件量=分配+转入），否则 statusField
                    const statusField = getStatusField(type, t);
                    const statusFieldsToUse = savedStatusFields?.length ? savedStatusFields : (statusField ? [statusField] : undefined);

                    await fetchDetailsByStatusFieldForDriver(
                        savedFilters.taskDate.format('YYYY-MM-DD'),
                        statusFieldsToUse,
                        pageRequest,
                        warehouseId,
                        undefined,
                        selectedDspIds,
                        selectedAreaIds,
                        selectedDriverIds,
                        undefined,
                        sortReqId
                    );
                } else if (isStatusType(type)) {
                    // 状态类型详情排序：使用详情筛选条件
                    // DSP/区域/司机下拉的选项统一来自主库远程搜索，此处只做名称→ID 换算
                    const detailDsps = dsps;
                    const selectedDspIds = detailFilters.selectedDsps.length > 0
                        ? (() => {
                            const ids = detailFilters.selectedDsps.map(dspName => {
                                const dsp = detailDsps.find((d: any) => d.name === dspName);
                                return dsp ? dsp.id : null;
                            }).filter(id => id !== null) as number[];
                            // 如果转换后是空数组，返回 undefined
                            return ids.length > 0 ? ids : undefined;
                        })()
                        : undefined;

                    // DSP/区域/司机下拉的选项统一来自主库远程搜索，此处只做名称→ID 换算
                    const detailAreasForSort = areas;
                    const selectedAreaIds = detailFilters.selectedAreas.length > 0
                        ? (() => {
                            const ids = detailFilters.selectedAreas.map(areaName => {
                                const area = detailAreasForSort.find((a: any) => a.name === areaName);
                                return area ? area.id : null;
                            }).filter(id => id !== null) as number[];
                            // 如果转换后是空数组，返回 undefined
                            return ids.length > 0 ? ids : undefined;
                        })()
                        : undefined;

                    const selectedDriverIds = toDriverIds(detailFilters.selectedDrivers);

                    const statusField = getStatusField(type, t);
                    const statusFieldsForSort = savedStatusFields?.length ? savedStatusFields : (statusField ? [statusField] : undefined);

                    if (statusField || statusFieldsForSort?.length) {
                        // Daily 页面：判断保存的 viewMode 是否为司机维度
                        const isSavedDriverMode = savedFilters.viewMode === 'driver';
                        if (isSavedDriverMode) {
                            // 司机维度：使用司机维度接口（应派件量用 statusFields 分配+转入）
                            // DSP/区域/司机下拉的选项统一来自主库远程搜索，此处只做名称→ID 换算
                            const detailAreasForSort2 = areas;
                            const selectedAreaIdsForSort = detailFilters.selectedAreas.length > 0
                                ? (() => {
                                    const ids = detailFilters.selectedAreas.map(areaName => {
                                        const area = detailAreasForSort2.find((a: any) => a.name === areaName);
                                        return area ? area.id : null;
                                    }).filter(id => id !== null) as number[];
                                    // 如果转换后是空数组，返回 undefined
                                    return ids.length > 0 ? ids : undefined;
                                })()
                                : undefined;

                            await fetchDetailsByStatusFieldForDriver(
                                savedFilters.taskDate.format('YYYY-MM-DD'),
                                statusFieldsForSort,
                                pageRequest,
                                warehouseId,
                                undefined,
                                selectedDspIds,
                                selectedAreaIdsForSort,
                                selectedDriverIds,
                                undefined,
                                sortReqId
                            );
                        } else {
                            const selectedDriverIdsForSort = toDriverIds(detailFilters.selectedDrivers);
                            
                            await fetchDetailsByStatusField(
                                savedFilters.taskDate.format('YYYY-MM-DD'),
                                statusField ? [statusField] : undefined,
                                pageRequest,
                                warehouseId,
                                undefined,
                                undefined,
                                selectedDspIds,
                                selectedAreaIds,
                                selectedDriverIdsForSort,
                                undefined,
                                sortReqId
                            );
                        }
                    }
                } else if (currentDetailType === 'area' && 'areaId' in record) {
                    // 区域维度详情排序：使用详情筛选条件
                    // DSP/区域/司机下拉的选项统一来自主库远程搜索，此处只做名称→ID 换算
                    const detailDsps = dsps;
                    const selectedDspIds = detailFilters.selectedDsps.length > 0
                        ? (() => {
                            const ids = detailFilters.selectedDsps.map(dspName => {
                                const dsp = detailDsps.find((d: any) => d.name === dspName);
                                return dsp ? dsp.id : null;
                            }).filter(id => id !== null) as number[];
                            // 如果转换后是空数组，返回 undefined
                            return ids.length > 0 ? ids : undefined;
                        })()
                        : undefined;

                    const selectedAreaIds = detailFilters.selectedAreas.length > 0
                        ? detailFilters.selectedAreas.map(areaName => {
                            const area = areas.find((a: any) => a.name === areaName);
                            return area ? area.id : null;
                        }).filter(id => id !== null) as number[]
                        : undefined;

                    // 如果没有 statusField，使用 fetchDetailsByStatusField查询（不传statusField）
                    const selectedDriverIds = toDriverIds(detailFilters.selectedDrivers);
                    
                    await fetchDetailsByStatusField(
                        savedFilters.taskDate.format('YYYY-MM-DD'),
                        undefined,
                        pageRequest,
                        warehouseId,
                        undefined,
                        undefined,
                        selectedDspIds,
                        selectedAreaIds,
                        selectedDriverIds,
                        undefined,
                        sortReqId
                    );
                }
            } catch (error) {
                console.error('详情排序失败:', error);
            }
        }
    };

    // 处理过滤器更新
    const handleFiltersUpdate = useCallback((newFilters: TaskMonitoringFilters) => {
        // 如果切换了维度，需要重置页码和清空数据
        const isViewModeChanged = newFilters.viewMode !== filters.viewMode;
        
        // 如果日期变化了，清空所有筛选条件
        const isTaskDateChanged = !newFilters.taskDate.isSame(filters.taskDate, 'day');
        
        // 如果切换了维度，清空两个维度的数据，确保重新加载
        if (isViewModeChanged) {
            // 切换维度时，清空两个维度的查询数据，确保下次查询是全新的
            clearAreaData();
            clearDriverData();
            // 切换维度时重置为默认排序：应派件量从高到低
            setSortConfig({ sortBy: 'allocatedParcels', sortDirection: 'DESC' });
        }
        
        // 如果日期变化了，清空筛选条件
        if (isTaskDateChanged) {
            newFilters = {
                ...newFilters,
                selectedWarehouses: [],
                selectedDsps: [],
                selectedAreas: [],
                selectedDrivers: [],
                selectedCities: []
            };
        }
        
        // 先重置页码，再更新筛选条件
        // 这样 useEffect 会在状态更新后自动触发查询，确保使用最新的筛选条件
        setCurrentPage(1); // 重置到第一页
        setFilters(newFilters);
        
        // 注意：不在这里直接调用 handleAreaFilter，让 useEffect 自动处理查询
        // 这样可以确保状态更新完成后再查询，避免使用旧的状态值
    }, [filters.viewMode, filters.taskDate, clearAreaData, clearDriverData]);

    // Daily 页面：直接使用所有可访问的仓库列表
    const filteredWarehouses = accessibleWarehouses.map(whs => ({ id: whs.id || '', name: whs.name || '' }));

    return {
        filters,
        setFilters: handleFiltersUpdate,
        driverData,
        areaData,
        // Daily 页面：根据维度返回对应的 summaryData
        summaryData: isDriverDimension() ? driverSummaryData : areaSummaryData,
        cities,
        warehouses: filteredWarehouses,
        dsps,
        areas,
        drivers,
        drawerDrivers,
        // DSP/区域/司机下拉改为远程搜索，交给筛选组件驱动关键词
        onDspSearch: setDspKeyword,
        onAreaSearch: setAreaKeyword,
        onDriverSearch: setDriverKeyword,
        dspOptionsLoading,
        areaOptionsLoading,
        driverOptionsLoading,
        onDrawerDriverSearch: setDrawerDriverKeyword,
        drawerDriverOptionsLoading,
        drawerVisible,
        drawerTitle,
        drawerData,
        drawerColumns,
        setDrawerVisible,
        handleDetailClick,
        handleUndeliveredDistributionClick,
        handleDeliveryProgressClick,
        statusTypeOptions,
        handleDetailStatusTypeChange,
        dataUpdatedAtText,
        handlePageChange,
        handleDetailPageChange,
        handleSortChange,
        handleDetailSortChange,
        sortConfig,
        detailSortConfig,
        detailPagination,
        detailLoading,
        detailFilters,
        setDetailFilters,
        detailStatusTypes,
        loading: isDriverDimension() ? driverLoading : areaLoading,
        pagination: (isDriverDimension() ? driverApiPagination : areaApiPagination) ? {
            current: (isDriverDimension() ? driverApiPagination : areaApiPagination)!.page,
            pageSize: (isDriverDimension() ? driverApiPagination : areaApiPagination)!.size,
            total: (isDriverDimension() ? driverApiPagination : areaApiPagination)!.totalElements,
        } : null,
        tablePagination: (isDriverDimension() ? driverApiPagination : areaApiPagination) ? {
            page: (isDriverDimension() ? driverApiPagination : areaApiPagination)!.page,
            size: (isDriverDimension() ? driverApiPagination : areaApiPagination)!.size,
            totalElements: (isDriverDimension() ? driverApiPagination : areaApiPagination)!.totalElements,
            totalPages: (isDriverDimension() ? driverApiPagination : areaApiPagination)!.totalPages,
            hasNext: (isDriverDimension() ? driverApiPagination : areaApiPagination)!.hasNext,
            hasPrevious: (isDriverDimension() ? driverApiPagination : areaApiPagination)!.hasPrevious,
        } : null,
        orderDetailVisible,
        orderId,
        setOrderDetailVisible,
        showOrderDetail,
        warehouseId: String(warehouseId),
        lastDetailContext,
        dspMode: isDsp,
        currentDspId: dspId,
        getCurrentSummaryDownloadArgs: () => currentSummaryDownloadArgsRef.current,
    };
};